/**
 * UI-ONLY MOCK LAYER.
 *
 * While we design the user app with the backend switched off, `call()` in api.ts routes here instead
 * of the network whenever `NEXT_PUBLIC_UI_MOCKS=1`. Every screen then renders and every flow is
 * walkable with no services running.
 *
 * TWO rules keep this honest:
 *
 *   1. The fixtures mirror the BACKEND's real JSON shape (snake_case, formatted money strings), not
 *      the TypeScript types, because `call<T>()` only casts — the runtime shape is whatever the
 *      service actually returns. Match the service, or a screen that works here breaks the moment the
 *      backend is reconnected.
 *
 *   2. This file is DEV-ONLY and is a no-op unless the env flag is set. Flip the flag off and the app
 *      talks to the real gateway again, untouched. Nothing here is a control; it is a stage set.
 */

import { FixtureRefusal, tokenizationFixtures } from './mocks-tokenization';
import { fromBaseUnits, toBaseUnits } from './money';

const DEMO_ID = 'demo0001-0000-4000-8000-000000000001';
const _COUNTERPARTY_ID = 'demo0002-0000-4000-8000-000000000002';

const now = () => new Date();
const iso = (offsetMs = 0) => new Date(Date.now() + offsetMs).toISOString();
const HOUR = 3_600_000;
const DAY = 86_400_000;

/**
 * A fixture that REFUSES.
 *
 * Thrown by a handler and converted to a `LipaApiError` in api.ts, so a refused call fails the same
 * way under mocks as it does against the gateway. Without this, mock mode can only ever succeed, and
 * every refusal path in the UI goes unexercised until it meets a real backend -- which is exactly
 * how the withdraw screen came to have no error state at all.
 *
 * Declared here rather than imported from api.ts because api.ts already imports this module, and the
 * other direction would be a cycle.
 */
export class MockRefusal extends Error {
  constructor(
    readonly detail: {
      code: string;
      category: 'policy' | 'transient' | 'validation' | 'internal';
      message: string;
      retryable: boolean;
    },
  ) {
    super(detail.message);
    this.name = 'MockRefusal';
  }
}

/** KES-per-unit board, indicative (the backend is off, so we do not pretend it is live). */
const RATES: Record<string, number> = {
  KES: 1,
  BTC: 8_500_000,
  ETH: 245_000,
  USDT: 129.5,
  USDC: 129.48,
  SOL: 22_400,
  XRP: 78.5,
  BNB: 82_000,
};

const SCALE: Record<string, number> = {
  KES: 2,
  BTC: 8,
  ETH: 18,
  USDT: 6,
  USDC: 6,
  SOL: 9,
  XRP: 6,
  BNB: 8,
};

function fmt(major: number, asset: string): string {
  const dp = asset === 'KES' ? 2 : Math.min(SCALE[asset] ?? 2, 8);
  return major.toLocaleString('en-US', {
    minimumFractionDigits: asset === 'KES' ? 2 : 0,
    maximumFractionDigits: dp,
  });
}

function computeQuote(query: URLSearchParams) {
  const from = query.get('from') ?? 'BTC';
  const to = query.get('to') ?? 'KES';
  const amount = Number(query.get('amount') ?? '0');
  const rate = (RATES[from] ?? 1) / (RATES[to] ?? 1);
  const gross = amount * rate;
  const feeBps = 50;
  const fee = gross * (feeBps / 10_000);
  const receive = gross - fee;
  const money = (v: number, asset: string) => ({
    amount: String(Math.round(v * 10 ** (SCALE[asset] ?? 2))),
    asset,
    display: fmt(v, asset),
  });
  return {
    from,
    to,
    rate: String(rate),
    pay: money(amount, from),
    gross: money(gross, to),
    fee: money(fee, to),
    fee_bps: feeBps,
    receive: money(receive, to),
    source: 'simulator',
    as_of: iso(),
    live: false,
  };
}

/** The signed-in demo participant: a Tier 2 individual, active and whitelisted. */
const ME = {
  participant_id: DEMO_ID,
  tier: 'T2' as const,
  status: 'active',
  tiers: [
    { tier: 'T0', label: 'Unverified', unlocks: ['Browse the app'] },
    { tier: 'T1', label: 'Identity verified', unlocks: ['Receive money', 'Hold a wallet'] },
    {
      tier: 'T2',
      label: 'Address verified',
      unlocks: ['Send money', 'Convert assets', 'Tokenize'],
    },
    { tier: 'T3', label: 'Business (KYB)', unlocks: ['Partner API', 'Merchant payments'] },
  ],
};

/**
 * NO GROUP SEPARATOR IN A BALANCE. `Money.format()` in `lipa.contracts` emits "225000.00" and never
 * "225,000.00", and rule 1 at the top of this file says these fixtures mirror the BACKEND, not what
 * looks tidy on a screen.
 *
 * This one character was a crash. The withdraw screen parsed the KES balance into minor units, and
 * `BigInt("225,000")` throws `SyntaxError: Cannot convert 225,000 to a BigInt` -- during render,
 * inside a `useMemo`, so it escaped the component and came out as the router's own "Unexpected
 * Application Error" page. A mock that disagrees with the wire produces exactly this: a bug that
 * exists only where nobody is looking for it. Grouping belongs in `formatMoneyAmount`, at the edge.
 */
const WALLET = {
  participant_id: DEMO_ID,
  frozen: false,
  balances: [
    { asset: 'KES', available: '225000.00', locked: '0.00', total: '225000.00' },
    { asset: 'BTC', available: '0.02500000', locked: '0.00000000', total: '0.02500000' },
    {
      asset: 'ETH',
      available: '0.840000000000000000',
      locked: '0.000000000000000000',
      total: '0.840000000000000000',
    },
    { asset: 'USDT', available: '150.000000', locked: '0.000000', total: '150.000000' },
    { asset: 'USDC', available: '75.000000', locked: '0.000000', total: '75.000000' },
    { asset: 'SOL', available: '12.500000000', locked: '0.000000000', total: '12.500000000' },
    { asset: 'XRP', available: '400.000000', locked: '0.000000', total: '400.000000' },
    { asset: 'BNB', available: '1.20000000', locked: '0.00000000', total: '1.20000000' },
  ],
};

const LIMITS = {
  available: true,
  asset: 'KES',
  phase: 'P3',
  per_transaction: { limit: '5000000' }, // KES 50,000.00
  daily: {
    limit: '20000000', // KES 200,000.00
    used: '2500000', // KES 25,000.00
    remaining: '17500000',
    resets_at: iso(8 * HOUR),
  },
  cumulative: {
    limit: '100000000', // KES 1,000,000.00
    used: '12000000', // KES 120,000.00
    remaining: '88000000',
    resets_at: null,
  },
};

const NOTIFICATIONS = [
  {
    id: 'n1',
    category: 'transaction',
    subject: 'Payment sent',
    content: 'You sent KES 5,000.00 to Amara W. It settled and is complete.',
    mandatory: false,
    read: false,
    acknowledged: false,
    at: iso(-2 * HOUR),
  },
  {
    id: 'n2',
    category: 'security.login',
    subject: 'New sign-in on this account',
    content:
      'A new device signed in from Nairobi. If this was not you, sign out everywhere from Profile.',
    mandatory: true,
    read: false,
    acknowledged: false,
    at: iso(-1 * DAY),
  },
  {
    id: 'n3',
    category: 'wallet',
    subject: 'Funds received',
    content: 'You received KES 25,000.00 from Zoe Holdings.',
    mandatory: false,
    read: true,
    acknowledged: false,
    at: iso(-3 * DAY),
  },
];

// NOTE: `correlationId` is camelCase to match what api.history's type and the components read. The
// real backend currently returns `correlation_id` (snake) here, an inconsistency to reconcile when the
// backend is wired back in; the mock follows the UI's expectation so links work in UI-only mode.
const HISTORY = {
  transactions: [
    {
      id: 't1',
      type: 'send',
      state: 'settled',
      direction: 'out',
      asset: 'KES',
      amount: '5,000.00',
      correlationId: 'c1',
      at: iso(-2 * HOUR),
    },
    {
      id: 't2',
      type: 'receive',
      state: 'settled',
      direction: 'in',
      asset: 'KES',
      amount: '25,000.00',
      correlationId: 'c2',
      at: iso(-3 * DAY),
    },
    {
      id: 't3',
      type: 'send',
      state: 'settled',
      direction: 'out',
      asset: 'KES',
      amount: '1,200.00',
      correlationId: 'c3',
      at: iso(-5 * DAY),
    },
    {
      id: 't4',
      type: 'convert',
      state: 'settled',
      direction: 'out',
      asset: 'BTC',
      amount: '0.01000000',
      correlationId: 'c4',
      at: iso(-8 * DAY),
    },
  ],
};

const DISPUTES = [
  {
    case_id: 'D-2026-0042',
    category: 'payment_not_received',
    priority: 'normal',
    status: 'investigating',
    subject: 'Payment to Amara W not received',
    detail: 'I sent KES 5,000 but the recipient says it has not arrived.',
    correlation_id: 'c1',
    opened_at: iso(-1 * DAY),
    sla_due_at: iso(2 * DAY),
    resolved_at: null,
    resolution: null,
    compensation: null,
  },
];

const DISCLOSURES = [
  {
    type: 'terms',
    version: 'v1.0',
    title: 'Terms of Service',
    body: 'The agreement between you and VASP.',
    acknowledged: true,
    acknowledged_version: 'v1.0',
    acknowledged_at: iso(-30 * DAY),
  },
  {
    type: 'privacy',
    version: 'v1.0',
    title: 'Privacy Notice',
    body: 'How your personal data is handled under the Kenya Data Protection Act.',
    acknowledged: true,
    acknowledged_version: 'v1.0',
    acknowledged_at: iso(-30 * DAY),
  },
  {
    type: 'risk',
    version: 'v1.1',
    title: 'Virtual Asset Risk Disclosure',
    body: 'Virtual assets are volatile. You can lose value. Read this before converting.',
    acknowledged: false,
    acknowledged_version: null,
    acknowledged_at: null,
  },
];

const SESSIONS = [
  {
    session_id: 's1',
    device_name: 'This device',
    platform: 'Web (Chrome)',
    ip_address: '41.90.x.x',
    started_at: iso(-2 * HOUR),
    last_active_at: iso(),
  },
  {
    session_id: 's2',
    device_name: 'iPhone 15',
    platform: 'iOS',
    ip_address: '41.90.x.x',
    started_at: iso(-5 * DAY),
    last_active_at: iso(-1 * DAY),
  },
];

const MY_ASSETS = [
  {
    asset_id: 'a1',
    class: 'land',
    ownership_reference: 'LR/NRB/BLOCK-12/PLOT-4471',
    verification_status: 'tokenized',
    token_id: 'tok-1',
    submitted_at: iso(-20 * DAY),
  },
  {
    asset_id: 'a2',
    class: 'vehicle',
    ownership_reference: 'KDL 442X',
    verification_status: 'under_review',
    token_id: null,
    submitted_at: iso(-4 * DAY),
  },
];

// ---------------------------------------------------------------------------
// Marketplace + Investor Portal (the super-app additions). No external images: every card carries a
// gradient tone and a category, so nothing here reaches a CDN. Yields and funding are illustrative.
// ---------------------------------------------------------------------------

// A broad demo set: one or more listings in every supported asset category, so category filters,
// cards, and cross-app visibility can all be tested. The first six ids are referenced by the portfolio,
// secondary market, and credit collateral, so they are kept stable. Tones cycle the 6 gradient keys.
const LISTINGS = [
  {
    id: 'lst-nyali',
    image: '/demo/assets/nyali.svg',
    title: 'Nyali Beachfront Apartments',
    category: 'Real Estate',
    location: 'Mombasa',
    tone: 'emerald',
    yieldPct: 12.5,
    fundedPct: 68,
    target: '25,000,000.00',
    raised: '17,000,000.00',
    pricePerToken: '5,000.00',
    minInvest: '5,000.00',
    risk: 'medium',
    trust: 'Nyali Property Trust',
    investors: 214,
  },
  {
    id: 'lst-solar',
    image: '/demo/assets/solar.svg',
    title: 'Turkana Solar Farm',
    category: 'Renewable Energy',
    location: 'Turkana',
    tone: 'amber',
    yieldPct: 15.0,
    fundedPct: 41,
    target: '40,000,000.00',
    raised: '16,400,000.00',
    pricePerToken: '2,500.00',
    minInvest: '2,500.00',
    risk: 'medium',
    trust: 'Turkana Energy Trust',
    investors: 132,
  },
  {
    id: 'lst-coffee',
    image: '/demo/assets/coffee.svg',
    title: 'Nyeri Coffee Estate',
    category: 'Agriculture',
    location: 'Nyeri',
    tone: 'lime',
    yieldPct: 9.8,
    fundedPct: 88,
    target: '12,000,000.00',
    raised: '10,560,000.00',
    pricePerToken: '1,000.00',
    minInvest: '1,000.00',
    risk: 'low',
    trust: 'Nyeri Agri Trust',
    investors: 421,
  },
  {
    id: 'lst-fleet',
    image: '/demo/assets/fleet.svg',
    title: 'Boda Fleet Financing',
    category: 'Vehicles',
    location: 'Nairobi',
    tone: 'sky',
    yieldPct: 18.0,
    fundedPct: 23,
    target: '8,000,000.00',
    raised: '1,840,000.00',
    pricePerToken: '2,000.00',
    minInvest: '2,000.00',
    risk: 'high',
    trust: 'Mobility Trust I',
    investors: 58,
  },
  {
    id: 'lst-invoice',
    image: '/demo/assets/invoice.svg',
    title: 'SME Invoice Pool',
    category: 'Receivables',
    location: 'Nationwide',
    tone: 'violet',
    yieldPct: 14.2,
    fundedPct: 55,
    target: '15,000,000.00',
    raised: '8,250,000.00',
    pricePerToken: '1,000.00',
    minInvest: '1,000.00',
    risk: 'medium',
    trust: 'Working Capital Trust',
    investors: 176,
  },
  {
    id: 'lst-warehouse',
    image: '/demo/assets/warehouse.svg',
    title: 'Athi River Warehouse',
    category: 'Warehouses',
    location: 'Machakos',
    tone: 'teal',
    yieldPct: 11.0,
    fundedPct: 72,
    target: '30,000,000.00',
    raised: '21,600,000.00',
    pricePerToken: '5,000.00',
    minInvest: '5,000.00',
    risk: 'low',
    trust: 'Logistics Property Trust',
    investors: 289,
  },
  {
    id: 'lst-westlands',
    image: '/demo/assets/westlands.svg',
    title: 'Westlands Office Tower',
    category: 'Commercial Property',
    location: 'Nairobi',
    tone: 'sky',
    yieldPct: 10.4,
    fundedPct: 61,
    target: '120,000,000.00',
    raised: '73,200,000.00',
    pricePerToken: '10,000.00',
    minInvest: '10,000.00',
    risk: 'low',
    trust: 'Westlands Commercial Trust',
    investors: 512,
  },
  {
    id: 'lst-kajiado',
    image: '/demo/assets/kajiado.svg',
    title: 'Kajiado Ranch Land',
    category: 'Land',
    location: 'Kajiado',
    tone: 'lime',
    yieldPct: 7.5,
    fundedPct: 34,
    target: '18,000,000.00',
    raised: '6,120,000.00',
    pricePerToken: '2,000.00',
    minInvest: '2,000.00',
    risk: 'medium',
    trust: 'Kajiado Land Trust',
    investors: 96,
  },
  {
    id: 'lst-cattle',
    image: '/demo/assets/cattle.svg',
    title: 'Laikipia Cattle Ranch',
    category: 'Livestock',
    location: 'Laikipia',
    tone: 'amber',
    yieldPct: 13.6,
    fundedPct: 47,
    target: '9,000,000.00',
    raised: '4,230,000.00',
    pricePerToken: '1,500.00',
    minInvest: '1,500.00',
    risk: 'medium',
    trust: 'Laikipia Livestock Trust',
    investors: 141,
  },
  {
    id: 'lst-cranes',
    image: '/demo/assets/cranes.svg',
    title: 'Mombasa Port Cranes',
    category: 'Equipment',
    location: 'Mombasa',
    tone: 'violet',
    yieldPct: 16.2,
    fundedPct: 29,
    target: '60,000,000.00',
    raised: '17,400,000.00',
    pricePerToken: '5,000.00',
    minInvest: '5,000.00',
    risk: 'high',
    trust: 'Coastal Equipment Trust',
    investors: 74,
  },
  {
    id: 'lst-tollroad',
    image: '/demo/assets/tollroad.svg',
    title: 'Nairobi Expressway Segment',
    category: 'Infrastructure',
    location: 'Nairobi',
    tone: 'teal',
    yieldPct: 9.1,
    fundedPct: 83,
    target: '200,000,000.00',
    raised: '166,000,000.00',
    pricePerToken: '10,000.00',
    minInvest: '10,000.00',
    risk: 'low',
    trust: 'Infrastructure Trust A',
    investors: 903,
  },
  {
    id: 'lst-tea',
    image: '/demo/assets/tea.svg',
    title: 'Kericho Tea Consignment',
    category: 'Commodities',
    location: 'Kericho',
    tone: 'emerald',
    yieldPct: 8.9,
    fundedPct: 76,
    target: '6,000,000.00',
    raised: '4,560,000.00',
    pricePerToken: '1,000.00',
    minInvest: '1,000.00',
    risk: 'low',
    trust: 'Highlands Commodity Trust',
    investors: 268,
  },
  {
    id: 'lst-gold',
    image: '/demo/assets/gold.svg',
    title: 'Gold Bullion Vault',
    category: 'Commodities',
    location: 'Nairobi',
    tone: 'amber',
    yieldPct: 6.2,
    fundedPct: 52,
    target: '50,000,000.00',
    raised: '26,000,000.00',
    pricePerToken: '5,000.00',
    minInvest: '5,000.00',
    risk: 'low',
    trust: 'Precious Metals Trust',
    investors: 387,
  },
  {
    id: 'lst-carbon',
    image: '/demo/assets/carbon.svg',
    title: 'Aberdare Reforestation Credits',
    category: 'Carbon Credits',
    location: 'Nyandarua',
    tone: 'lime',
    yieldPct: 11.8,
    fundedPct: 44,
    target: '10,000,000.00',
    raised: '4,400,000.00',
    pricePerToken: '500.00',
    minInvest: '500.00',
    risk: 'medium',
    trust: 'Aberdare Carbon Trust',
    investors: 205,
  },
  {
    id: 'lst-avocado',
    image: '/demo/assets/avocado.svg',
    title: 'Meru Avocado Orchard',
    category: 'Agriculture',
    location: 'Meru',
    tone: 'lime',
    yieldPct: 12.9,
    fundedPct: 63,
    target: '8,500,000.00',
    raised: '5,355,000.00',
    pricePerToken: '1,000.00',
    minInvest: '1,000.00',
    risk: 'medium',
    trust: 'Meru Agri Trust',
    investors: 231,
  },
  {
    id: 'lst-retail',
    image: '/demo/assets/retail.svg',
    title: 'Sokoni Retail Chain',
    category: 'Private Equity',
    location: 'Nairobi',
    tone: 'sky',
    yieldPct: 17.5,
    fundedPct: 38,
    target: '35,000,000.00',
    raised: '13,300,000.00',
    pricePerToken: '5,000.00',
    minInvest: '5,000.00',
    risk: 'high',
    trust: 'Sokoni Ventures Trust',
    investors: 118,
  },
  {
    id: 'lst-villas',
    image: '/demo/assets/villas.svg',
    title: 'Kilifi Holiday Villas',
    category: 'Real Estate',
    location: 'Kilifi',
    tone: 'teal',
    yieldPct: 13.2,
    fundedPct: 57,
    target: '22,000,000.00',
    raised: '12,540,000.00',
    pricePerToken: '5,000.00',
    minInvest: '5,000.00',
    risk: 'medium',
    trust: 'Kilifi Hospitality Trust',
    investors: 194,
  },
  {
    id: 'lst-art',
    image: '/demo/assets/art.svg',
    title: 'Nairobi Contemporary Art Fund',
    category: 'Fine Art',
    location: 'Nairobi',
    tone: 'violet',
    yieldPct: 8.0,
    fundedPct: 26,
    target: '14,000,000.00',
    raised: '3,640,000.00',
    pricePerToken: '2,500.00',
    minInvest: '2,500.00',
    risk: 'high',
    trust: 'Nairobi Art Trust',
    investors: 63,
  },
];

const MARKET_CATEGORIES = [
  'All',
  'Real Estate',
  'Commercial Property',
  'Land',
  'Agriculture',
  'Livestock',
  'Renewable Energy',
  'Infrastructure',
  'Vehicles',
  'Equipment',
  'Warehouses',
  'Commodities',
  'Carbon Credits',
  'Receivables',
  'Private Equity',
  'Fine Art',
];

const PORTFOLIO = {
  value: '182,400.00',
  invested: '150,000.00',
  returnsPct: 21.6,
  incomeToDate: '12,300.00',
  projectedAnnual: '19,800.00',
  riskScore: 'Moderate',
  allocation: [
    { label: 'Real Estate', pct: 42, tone: 'emerald' },
    { label: 'Agriculture', pct: 23, tone: 'lime' },
    { label: 'Energy', pct: 18, tone: 'amber' },
    { label: 'Receivables', pct: 17, tone: 'violet' },
  ],
  holdings: [
    {
      id: 'lst-nyali',
      image: '/demo/assets/nyali.svg',
      title: 'Nyali Beachfront Apartments',
      category: 'Real Estate',
      units: 15,
      value: '78,000.00',
      returnPct: 24.1,
      income: '6,200.00',
    },
    {
      id: 'lst-coffee',
      image: '/demo/assets/coffee.svg',
      title: 'Nyeri Coffee Estate',
      category: 'Agriculture',
      units: 42,
      value: '46,400.00',
      returnPct: 11.5,
      income: '3,100.00',
    },
    {
      id: 'lst-solar',
      image: '/demo/assets/solar.svg',
      title: 'Turkana Solar Farm',
      category: 'Renewable Energy',
      units: 20,
      value: '34,000.00',
      returnPct: 16.0,
      income: '2,100.00',
    },
    {
      id: 'lst-invoice',
      image: '/demo/assets/invoice.svg',
      title: 'SME Invoice Pool',
      category: 'Receivables',
      units: 24,
      value: '24,000.00',
      returnPct: 14.2,
      income: '900.00',
    },
  ],
  income: [
    {
      source: 'Nyali Property Trust',
      kind: 'Rental income',
      amount: '2,400.00',
      at: iso(-4 * DAY),
    },
    { source: 'Nyeri Agri Trust', kind: 'Profit share', amount: '1,750.00', at: iso(-12 * DAY) },
    {
      source: 'Turkana Energy Trust',
      kind: 'Distribution',
      amount: '1,100.00',
      at: iso(-25 * DAY),
    },
  ],
};

// Earnings & Distributions (Phase 2). Illustrative income across the holder's investments.
// Ramp history, MUTABLE for the session: a withdrawal made during a demo appears in the list
// afterwards rather than vanishing, which is what makes the flow feel like it did something.
const MOCK_RAMPS: {
  instruction_id: string;
  direction: string;
  rail: string;
  amount: string;
  asset: string;
  state: string;
  provider_reference: string | null;
  failure_reason: string | null;
  created_at: string;
  confirmed_at: string | null;
}[] = [
  {
    instruction_id: 'ri-2026-0007',
    direction: 'withdrawal',
    rail: 'mobile_money',
    // Unformatted, like every other amount on the wire. See the note on WALLET above.
    amount: '8000.00',
    asset: 'KES',
    state: 'settled',
    provider_reference: 'MPX7K21QZ4',
    failure_reason: null,
    created_at: iso(-9 * DAY),
    confirmed_at: iso(-9 * DAY),
  },
  {
    instruction_id: 'ri-2026-0004',
    direction: 'deposit',
    rail: 'bank_transfer',
    amount: '35000.00',
    asset: 'KES',
    state: 'settled',
    provider_reference: 'KCB-88213',
    failure_reason: null,
    created_at: iso(-21 * DAY),
    confirmed_at: iso(-21 * DAY),
  },
];

const EARNINGS = {
  totalEarned: '12,300.00',
  thisMonth: '2,400.00',
  projectedAnnual: '19,800.00',
  available: '4,250.00',
  byType: [
    { type: 'Rental income', amount: '6,200.00', pct: 50, tone: 'emerald' },
    { type: 'Profit share', amount: '3,100.00', pct: 25, tone: 'lime' },
    { type: 'Distributions', amount: '2,100.00', pct: 17, tone: 'amber' },
    { type: 'Interest', amount: '900.00', pct: 8, tone: 'violet' },
  ],
  distributions: [
    {
      source: 'Nyali Property Trust',
      kind: 'Rental income',
      amount: '2,400.00',
      at: iso(-4 * DAY),
    },
    { source: 'Nyeri Agri Trust', kind: 'Profit share', amount: '1,750.00', at: iso(-12 * DAY) },
    {
      source: 'Turkana Energy Trust',
      kind: 'Distribution',
      amount: '1,100.00',
      at: iso(-25 * DAY),
    },
    { source: 'Working Capital Trust', kind: 'Interest', amount: '900.00', at: iso(-32 * DAY) },
    {
      source: 'Nyali Property Trust',
      kind: 'Rental income',
      amount: '2,300.00',
      at: iso(-38 * DAY),
    },
  ],
  upcoming: [
    {
      source: 'Nyali Property Trust',
      kind: 'Rental income',
      estAmount: '2,400.00',
      dueDate: iso(20 * DAY),
    },
    {
      source: 'Nyeri Agri Trust',
      kind: 'Profit share',
      estAmount: '1,800.00',
      dueDate: iso(45 * DAY),
    },
  ],
};

// Secondary market (Phase 2). Peer trading of already-issued tokens: last price, 24h move, book, and
// how many units the holder owns (so they can list to sell).
const MARKET = [
  {
    id: 'lst-nyali',
    image: '/demo/assets/nyali.svg',
    title: 'Nyali Beachfront Apartments',
    category: 'Real Estate',
    tone: 'emerald',
    lastPrice: '5,120.00',
    change24h: 2.4,
    bid: '5,080.00',
    ask: '5,150.00',
    volume24h: '1.2M',
    yourUnits: 15,
  },
  {
    id: 'lst-coffee',
    image: '/demo/assets/coffee.svg',
    title: 'Nyeri Coffee Estate',
    category: 'Agriculture',
    tone: 'lime',
    lastPrice: '1,010.00',
    change24h: -1.1,
    bid: '1,000.00',
    ask: '1,020.00',
    volume24h: '640K',
    yourUnits: 42,
  },
  {
    id: 'lst-solar',
    image: '/demo/assets/solar.svg',
    title: 'Turkana Solar Farm',
    category: 'Renewable Energy',
    tone: 'amber',
    lastPrice: '2,540.00',
    change24h: 0.8,
    bid: '2,510.00',
    ask: '2,560.00',
    volume24h: '410K',
    yourUnits: 20,
  },
  {
    id: 'lst-invoice',
    image: '/demo/assets/invoice.svg',
    title: 'SME Invoice Pool',
    category: 'Receivables',
    tone: 'violet',
    lastPrice: '1,005.00',
    change24h: 5.2,
    bid: '1,000.00',
    ask: '1,010.00',
    volume24h: '820K',
    yourUnits: 24,
  },
  {
    id: 'lst-warehouse',
    image: '/demo/assets/warehouse.svg',
    title: 'Athi River Warehouse',
    category: 'Warehouses',
    tone: 'teal',
    lastPrice: '5,060.00',
    change24h: -0.6,
    bid: '5,020.00',
    ask: '5,090.00',
    volume24h: '300K',
    yourUnits: 0,
  },
  {
    id: 'lst-fleet',
    image: '/demo/assets/fleet.svg',
    title: 'Boda Fleet Financing',
    category: 'Vehicles',
    tone: 'sky',
    lastPrice: '2,020.00',
    change24h: 1.9,
    bid: '2,000.00',
    ask: '2,040.00',
    volume24h: '180K',
    yourUnits: 0,
  },
];

// Tokenization / issuance (the sponsor side). The sponsor NEVER mints: they submit an asset, the
// Holding Entity takes legal title, the Trust generates the capital structure and mints, and the
// sponsor approves it, then decides how much of their allocation to bring to market as offerings.
const _ASSET_TYPES = [
  'Property',
  'Land',
  'Vehicle',
  'Commodity',
  'Business',
  'Receivable',
  'Equipment',
  'Carbon Credits',
  'Energy Project',
  'Agriculture',
];

const SPONSOR_ASSETS = [
  {
    id: 'sa-green-valley',
    name: 'Green Valley Apartments',
    type: 'Property',
    category: 'Residential Real Estate',
    location: 'Kileleshwa, Nairobi',
    estimatedValue: '100,000,000.00',
    stage: 'offering', // draft | submitted | diligence | valuation | formation | allocation | offering
    trust: {
      id: 'trust-green-valley',
      name: 'Green Valley Property Trust',
      holdingEntity: 'VAST Holding Entity',
      trustNumber: 'KE-TR-2026-0041',
    },
    review: [
      { key: 'diligence', label: 'Due diligence', status: 'verified' },
      { key: 'valuation', label: 'Independent valuation', status: 'verified' },
      { key: 'formation', label: 'Trust formation', status: 'verified' },
      { key: 'holding', label: 'Holding entity + title', status: 'verified' },
      { key: 'approval', label: 'Issuance approval', status: 'verified' },
    ],
    valuation: {
      independent: '97,800,000.00',
      approved: '95,000,000.00',
      valuer: 'Knight Frank Kenya',
      date: iso(-10 * DAY),
    },
    capital: {
      approvedValue: '95,000,000.00',
      totalSupply: 950000,
      tokenPrice: '100.00',
      sponsorAllocation: 570000,
      marketplaceAllocation: 380000,
      reserve: 0,
      approvedBySponsor: true,
    },
    treasury: {
      total: 950000,
      sponsorTreasury: 570000,
      marketplaceInventory: 300000,
      investorHoldings: 80000,
      reserved: 0,
      burned: 0,
    },
    offerings: [
      {
        id: 'off-1',
        name: 'Offering #1',
        tokens: 100000,
        price: '100.00',
        sold: 80000,
        opens: iso(-20 * DAY),
        closes: iso(10 * DAY),
        status: 'live',
      },
      {
        id: 'off-2',
        name: 'Offering #2',
        tokens: 50000,
        price: '110.00',
        sold: 0,
        opens: iso(15 * DAY),
        closes: iso(45 * DAY),
        status: 'draft',
      },
    ],
    capitalRaised: '8,000,000.00',
  },
  {
    id: 'sa-boda',
    name: 'Boda Fleet Financing',
    type: 'Vehicle',
    category: 'Vehicles',
    location: 'Nairobi',
    estimatedValue: '8,000,000.00',
    stage: 'valuation',
    trust: { id: null, name: null, holdingEntity: 'VAST Holding Entity', trustNumber: null },
    review: [
      { key: 'diligence', label: 'Due diligence', status: 'verified' },
      { key: 'valuation', label: 'Independent valuation', status: 'in review' },
      { key: 'formation', label: 'Trust formation', status: 'pending' },
      { key: 'holding', label: 'Holding entity + title', status: 'pending' },
      { key: 'approval', label: 'Issuance approval', status: 'pending' },
    ],
    valuation: null,
    capital: null,
    treasury: null,
    offerings: [],
    capitalRaised: '0.00',
  },
];

function sponsorSummary() {
  return {
    assets: SPONSOR_ASSETS.length,
    trusts: SPONSOR_ASSETS.filter((a) => a.trust.id).length,
    liveOfferings: SPONSOR_ASSETS.flatMap((a) => a.offerings).filter((o) => o.status === 'live')
      .length,
    capitalRaised: '8,000,000.00',
    unsoldTokens: SPONSOR_ASSETS.reduce((n, a) => n + (a.treasury?.sponsorTreasury ?? 0), 0),
  };
}

// Every offering across every asset, flattened, so a sponsor sees their fundraising in one place.
function sponsorOfferings() {
  return {
    offerings: SPONSOR_ASSETS.flatMap((a) =>
      a.offerings.map((o) => ({ ...o, asset: a.name, assetId: a.id })),
    ),
  };
}

// Distributions the Trust has paid to investors out of an asset's income. The sponsor sees what their
// asset returned, so income-sharing is visible rather than a promise on a listing.
const SPONSOR_DISTRIBUTIONS = {
  paidToDate: '3,840,000.00',
  nextRun: iso(9 * DAY),
  history: [
    {
      id: 'd-1',
      asset: 'Green Valley Apartments',
      assetId: 'sa-green-valley',
      period: 'July 2026',
      kind: 'Rental income',
      gross: '1,280,000.00',
      toInvestors: '512,000.00',
      toSponsor: '768,000.00',
      at: iso(-2 * DAY),
      status: 'paid',
    },
    {
      id: 'd-2',
      asset: 'Green Valley Apartments',
      assetId: 'sa-green-valley',
      period: 'June 2026',
      kind: 'Rental income',
      gross: '1,280,000.00',
      toInvestors: '512,000.00',
      toSponsor: '768,000.00',
      at: iso(-32 * DAY),
      status: 'paid',
    },
    {
      id: 'd-3',
      asset: 'Green Valley Apartments',
      assetId: 'sa-green-valley',
      period: 'May 2026',
      kind: 'Rental income',
      gross: '1,280,000.00',
      toInvestors: '512,000.00',
      toSponsor: '768,000.00',
      at: iso(-62 * DAY),
      status: 'paid',
    },
    {
      id: 'd-4',
      asset: 'Green Valley Apartments',
      assetId: 'sa-green-valley',
      period: 'August 2026',
      kind: 'Rental income',
      gross: '1,280,000.00',
      toInvestors: '512,000.00',
      toSponsor: '768,000.00',
      at: iso(9 * DAY),
      status: 'upcoming',
    },
  ],
};

// P2P trades (Payments depth). Peer offers to buy/sell virtual assets for KES.
// The marketplace book, in the WIRE shape the backend serves.
//
// Deliberately matched field for field to `GET /p2p/offers`, because the last time these fixtures
// disagreed with the wire the TS type agreed with the FIXTURES and a broken link shipped (see the
// note on MovementResult in api.ts). A mock that confirms a bug is worse than no mock.
//
// `settles_automatically` is FALSE on every offer, which is not laziness: no payment method is one
// the platform settles, because every settlement adapter is a simulator (ADR 0003). A fixture that
// showed the fast path would be advertising a control that does not exist.
// Every method FALSE, matching the seeded catalogue: no settlement adapter is real (ADR 0003).
const P2P_METHODS = {
  methods: [
    { code: 'mobile_money', label: 'Mobile money', settles_automatically: false },
    { code: 'bank_transfer', label: 'Bank transfer', settles_automatically: false },
    { code: 'cash_deposit', label: 'Cash deposit at branch', settles_automatically: false },
    { code: 'lipa_wallet', label: 'VASP wallet', settles_automatically: false },
  ],
  automatic_settlement_available: false,
};

// ------------------------------------------------------------------------------------------------
// THE INSTANT SWAP BOARD (FR-U-LQD-004). What the platform will trade from its own float today.
//
// The board is the ONLY statement of what instant swap supports, which is why the screen derives its
// action list from it instead of holding a menu of assets. Publish a pair here and it appears there;
// publish nothing and the screen says so. An asset the treasury has not priced cannot be offered by
// a dropdown that was written months earlier.
//
// A pair whose quote is KES is a CASH purchase ("buy USDT with cash"); a pair whose quote is another
// virtual asset is a CONVERSION ("convert USDT to USDC"). Same shape, same endpoint: the difference
// is a property of the pair, not a second feature.
//
// Every asset below already exists in the wallet, the rate board and the P2P book. Nothing new is
// invented here, and no pair is published for an asset the app does not otherwise support.
// ------------------------------------------------------------------------------------------------

interface MockBoardRate {
  base: string;
  quote: string;
  /** What the participant pays per unit of `base`, in `quote`. Four decimal places, as served. */
  you_buy_at: string;
  you_sell_at: string;
  published_at: string;
}

const BOARD_PAIRS: [string, string, string, string][] = [
  // base, quote, you_buy_at, you_sell_at
  ['USDT', 'KES', '152.4000', '151.6000'],
  ['USDC', 'KES', '152.3000', '151.5000'],
  ['ETH', 'KES', '246500.0000', '243500.0000'],
  ['BTC', 'KES', '8560000.0000', '8460000.0000'],
  ['USDC', 'USDT', '1.0020', '0.9980'],
  ['USDT', 'USDC', '1.0020', '0.9980'],
];

function swapBoard() {
  const publishedAt = new Date(new Date().setHours(8, 0, 0, 0)).toISOString();
  const rates: MockBoardRate[] = BOARD_PAIRS.map(([base, quote, buy, sell]) => ({
    base,
    quote,
    you_buy_at: buy,
    you_sell_at: sell,
    published_at: publishedAt,
  }));
  return {
    date: new Date().toISOString().slice(0, 10),
    rates,
    fixed_for_the_day: true,
    //: Both halves of FR-U-LQD-004. Fixed AND withdrawable.
    may_be_withdrawn: true,
  };
}

/**
 * Price an amount against the board. INTEGER ARITHMETIC ONLY.
 *
 * This used to be `Math.floor((payMinor * 10_000) / 152.4)` with the rate written into the
 * expression, which was both a float division on money and a fixture that could only ever price one
 * pair. The scale factor between the two assets is the exact thing the real implementation got wrong
 * once, so it is carried here explicitly rather than smoothed into a tidier number.
 *
 *   receive = pay * 10^scale(to) * 10^4 / (rate_e4 * 10^scale(from))
 */
function priceSwap(body: { from?: string; to?: string; amount?: string }) {
  const from = body.from ?? 'KES';
  const to = body.to ?? 'USDT';
  const rate = swapBoard().rates.find((r) => r.base === to && r.quote === from);

  if (!rate) {
    // The real service refuses with `no_board_rate` rather than inventing a price, and the screen
    // has a state for it. A fixture that quoted anyway would hide that state until production.
    throw new MockRefusal({
      code: 'LIQUIDITY_UNAVAILABLE',
      category: 'policy',
      message: `No rate is published for ${to} against ${from} today. Nothing has moved.`,
      retryable: false,
    });
  }

  const scaleFrom = SCALE[from] ?? 2;
  const scaleTo = SCALE[to] ?? 2;
  const pay = toBaseUnits(String(body.amount ?? '0'), scaleFrom);

  const rateE4 = toBaseUnits(rate.you_buy_at, 4);
  const midE4 = (toBaseUnits(rate.you_buy_at, 4) + toBaseUnits(rate.you_sell_at, 4)) / 2n;

  const convert = (r: bigint) =>
    r === 0n ? 0n : (pay * 10n ** BigInt(scaleTo) * 10_000n) / (r * 10n ** BigInt(scaleFrom));

  const receives = convert(rateE4);
  const atMid = convert(midE4);
  const spread = atMid > receives ? atMid - receives : 0n;

  return {
    quote_id: `q-mock-${to}-${from}-${pay}`,
    from: { asset: from, amount: String(pay) },
    to: { asset: to, amount: String(receives) },
    rate: rate.you_buy_at,
    spread: String(spread),
    spread_bps: atMid > 0n ? Number((spread * 10_000n) / atMid) : 0,
    fee: '0',
    you_receive: String(receives),
    rate_basis: 'published_daily',
    board_date: new Date().toISOString().slice(0, 10),
    may_be_withdrawn: true,
    expires_at: new Date(Date.now() + 30_000).toISOString(),
  };
}

interface MockOffer {
  id: string;
  side: 'buy' | 'sell';
  asset: string;
  quote_asset: string;
  price: string;
  remaining: string;
  min: string;
  max: string;
  methods: string[];
  terms: string | null;
  release_path: 'rail_settled' | 'counterparty_confirmed';
  settles_automatically: boolean;
}

/**
 * The book, MUTABLE for the session, like MOCK_RAMPS.
 *
 * It used to be a frozen pair of offers and `POST /p2p/offers` answered with `P2P.offers[0]` -- so
 * posting an offer returned SOMEBODY ELSE'S offer, the screen navigated to a book that did not
 * contain the thing just posted, and the seller's only evidence of success was that nothing had
 * visibly failed. That is not a fixture standing in for a backend; it is a fixture disagreeing with
 * one, which rule 1 at the top of this file exists to stop.
 */
const P2P_BOOK: MockOffer[] = [
  {
    id: 'o1',
    side: 'sell',
    asset: 'USDT',
    quote_asset: 'KES',
    price: '129.80',
    remaining: '2400.000000',
    min: '10.000000',
    max: '1500.000000',
    methods: ['mobile_money', 'bank_transfer'],
    terms: 'Please pay within 30 minutes.',
    release_path: 'counterparty_confirmed',
    settles_automatically: false,
  },
  {
    id: 'o2',
    side: 'sell',
    asset: 'BTC',
    quote_asset: 'KES',
    price: '8510000.00',
    remaining: '0.35000000',
    min: '0.00100000',
    max: '0.20000000',
    methods: ['bank_transfer'],
    terms: null,
    release_path: 'counterparty_confirmed',
    settles_automatically: false,
  },
  {
    // A BUY-side offer, so the other tab is not permanently empty now that the book filters by side
    // the way `P2PEngine.book` does.
    id: 'o3',
    side: 'buy',
    asset: 'USDT',
    quote_asset: 'KES',
    price: '128.90',
    remaining: '5000.000000',
    min: '50.000000',
    max: '2000.000000',
    methods: ['mobile_money'],
    terms: 'Kindly send the asset as soon as my payment lands.',
    release_path: 'counterparty_confirmed',
    settles_automatically: false,
  },
];

/**
 * The book as the ENGINE serves it: `side` is what the CALLER wants to do, so it returns the
 * opposite side. Somebody asking to buy is shown offers to sell.
 */
function p2pBook(side: string) {
  const wanted = side === 'sell' ? 'buy' : 'sell';
  const offers = P2P_BOOK.filter((o) => o.side === wanted);
  return {
    offers,
    automatic_settlement_available: offers.some((o) => o.release_path === 'rail_settled'),
  };
}

/**
 * Post an offer, and REFUSE the things the real service refuses.
 *
 * `parse_money` server-side rejects more decimal places than the asset carries rather than
 * truncating them, and the engine will not take a minimum above a maximum. A fixture that accepted
 * both would let a screen ship without the validation, and the first person to meet the real
 * endpoint would get a 422 for a form the app had told them was fine.
 */
function createMockOffer(raw: unknown): MockOffer {
  const body = (raw ?? {}) as Record<string, string | string[] | undefined>;
  const asset = String(body.asset ?? 'USDT');
  const scale = SCALE[asset] ?? 2;

  const refuse = (message: string): never => {
    throw new MockRefusal({
      code: 'VALIDATION_FAILED',
      category: 'validation',
      message,
      retryable: false,
    });
  };

  const parse = (field: string, label: string, decimals: number): bigint => {
    try {
      return toBaseUnits(String(body[field] ?? ''), decimals);
    } catch (err) {
      return refuse(err instanceof Error ? `${label}: ${err.message}` : `${label} is not valid.`);
    }
  };

  const price = parse('price', 'Your price', 2);
  const total = parse('total', 'Total', scale);
  const minimum = parse('minimum', 'Smallest trade', scale);
  const maximum = parse('maximum', 'Largest trade', scale);
  const methods = Array.isArray(body.methods) ? body.methods : [];

  if (price <= 0n) refuse('Your price must be above zero.');
  if (total <= 0n) refuse('The total must be above zero.');
  if (methods.length === 0) refuse('Choose at least one way you will be paid.');
  if (minimum > maximum) refuse('The smallest trade cannot be larger than the largest trade.');
  if (maximum > total) refuse('The largest trade cannot be more than the total you are offering.');

  const catalogue = P2P_METHODS.methods;
  const automatic =
    methods.length > 0 &&
    methods.every((code) => catalogue.find((m) => m.code === code)?.settles_automatically);

  const offer: MockOffer = {
    id: `o-${(P2P_BOOK.length + 1).toString(36)}${Math.floor(Math.random() * 1e6).toString(36)}`,
    side: body.side === 'buy' ? 'buy' : 'sell',
    asset,
    quote_asset: String(body.quote_asset ?? 'KES'),
    price: fromBaseUnits(price, 2),
    remaining: fromBaseUnits(total, scale),
    min: fromBaseUnits(minimum, scale),
    max: fromBaseUnits(maximum, scale),
    methods: methods.map(String),
    terms: typeof body.terms === 'string' && body.terms.trim() ? body.terms.trim() : null,
    release_path: automatic ? 'rail_settled' : 'counterparty_confirmed',
    settles_automatically: automatic,
  };

  P2P_BOOK.unshift(offer);
  return offer;
}

// ------------------------------------------------------------------------------------------------
// USDT TILLS. The payment handle a participant hands out.
//
// There is NO `/tills` service in the backend, so unlike most of this file these fixtures do not
// mirror an existing wire shape: they PROPOSE one. That is a different claim and it is worth marking,
// because rule 1 above cannot be checked against anything here.
//
// Two properties are load-bearing and are enforced in the store rather than in a screen:
//
//   1. ONE ACTIVE TILL PER PARTICIPANT PER ASSET. A second POST returns the first till. A shopkeeper
//      with two till numbers has one printed on the counter and one collecting the money.
//   2. THE ADDRESS IS MARKED SIMULATED. Custody is an MPC simulator (CLAUDE.md gap 10), so nothing a
//      till shows can settle on a chain. The flag travels with the record so the screen cannot
//      forget to say so, and it is a server fact rather than a UI constant so it flips with custody.
//
// Persisted to localStorage so a till survives a page reload. A payment handle that vanished when
// the demo was refreshed would be a worse lie than no till at all.
// ------------------------------------------------------------------------------------------------

interface MockTill {
  id: string;
  till_number: string;
  participant_id: string;
  asset: string;
  network: string;
  network_tag: string;
  wallet_address: string;
  status: 'active' | 'inactive';
  created_at: string;
  address_is_simulated: boolean;
}

// The key carries a version because the till FORMAT has changed twice: `LP-4K7Q2M` became
// `LP-USDT-TRC20-4K7Q2M`, and the separators then came out for `LPUSDTTRC204K7Q2M`. A browser
// holding an old till would otherwise show a handle that no longer resolves, which is the worst of
// both states.
const TILL_STORE_KEY = 'lipa.mock.tills.v3';

/** No O, 0, I or 1: a till gets read down a phone line and dictated across a counter. */
const TILL_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

/**
 * WHICH ASSETS, AND WHICH CHAINS. Served, so no screen holds its own copy.
 *
 * The ASSETS are the platform's own: `ASSET_SCALE` in `backend/src/lipa/contracts/money.py` knows
 * KES, BTC, ETH, USDT and USDC and nothing else, and KES is fiat and has no chain. Offering SOL or
 * XRP here -- both of which sit in the wallet fixture -- would be offering a till for an asset the
 * ledger cannot represent.
 *
 * The NETWORKS are a proposal. Nothing in `backend/src/lipa` knows what a network is: custody is an
 * MPC simulator (CLAUDE.md gap 10), so no chain is live and every entry says so through `live:false`.
 * That flag is the disclosure, and it is served rather than written into a screen so the day a
 * custodian is contracted the answer changes in one place.
 *
 * The pairs themselves are the standard ones a Kenyan user would expect to see on an exchange, and
 * the tags are the token standards they are named by everywhere else (TRC20, ERC20, BEP20). Using our
 * own names for them would be inventing vocabulary for a thing people already know.
 */
const TILL_ASSETS: {
  asset: string;
  name: string;
  networks: { tag: string; label: string; live: boolean; note?: string }[];
}[] = [
  {
    asset: 'USDT',
    name: 'Tether',
    networks: [
      {
        tag: 'TRC20',
        label: 'Tron',
        live: false,
        note: 'Lowest fees, and what most Kenyan traders use.',
      },
      { tag: 'ERC20', label: 'Ethereum', live: false, note: 'Widest support, highest fees.' },
      { tag: 'BEP20', label: 'BNB Smart Chain', live: false },
    ],
  },
  {
    asset: 'USDC',
    name: 'USD Coin',
    networks: [
      { tag: 'ERC20', label: 'Ethereum', live: false },
      { tag: 'POLYGON', label: 'Polygon', live: false, note: 'Low fees, fast confirmation.' },
    ],
  },
  {
    asset: 'BTC',
    name: 'Bitcoin',
    networks: [{ tag: 'BTC', label: 'Bitcoin', live: false, note: 'The Bitcoin network only.' }],
  },
  {
    asset: 'ETH',
    name: 'Ethereum',
    networks: [{ tag: 'ERC20', label: 'Ethereum', live: false }],
  },
];

/**
 * MEMORY IS THE STORE; localStorage is only how it survives a reload.
 *
 * Reading straight from localStorage looks simpler and is wrong: in a private window, with storage
 * disabled, or under any runtime without a DOM, every read returns nothing, so a till is written and
 * immediately lost. The one-till-per-pair rule then issues a fresh number on every press, and the
 * till just created does not resolve. The session must work when persistence does not.
 */
let tillCache: MockTill[] | null = null;

function readTills(): MockTill[] {
  if (tillCache) return tillCache;
  try {
    const raw = globalThis.localStorage?.getItem(TILL_STORE_KEY);
    tillCache = raw ? (JSON.parse(raw) as MockTill[]) : [];
  } catch {
    // A private-mode browser or a wiped profile is not an error worth surfacing in a stage set.
    tillCache = [];
  }
  return tillCache;
}

function writeTills(tills: MockTill[]): void {
  tillCache = tills;
  try {
    globalThis.localStorage?.setItem(TILL_STORE_KEY, JSON.stringify(tills));
  } catch {
    /* the session keeps working; only persistence across a reload is lost */
  }
}

/** Separators are not part of a till. Somebody typing the old hyphenated form still finds it. */
function tillKey(input: string): string {
  return input.toUpperCase().replace(/[^A-Z0-9]/g, '');
}

function randomFrom(alphabet: string, length: number): string {
  const bytes = new Uint8Array(length);
  globalThis.crypto?.getRandomValues?.(bytes);
  return Array.from(bytes, (b) => alphabet[b % alphabet.length]).join('');
}

/**
 * An address SHAPED like the chain it belongs to.
 *
 * A Tron address is base58 beginning with T; a Bitcoin one is bech32 beginning with bc1. Handing a
 * `0x...` address to every chain would make the network choice look cosmetic, which is exactly the
 * misunderstanding that loses people their money. Every one of these is still simulated and flagged.
 */
function simulatedAddress(networkTag: string): string {
  if (networkTag === 'TRC20') {
    return `T${randomFrom('ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz123456789', 33)}`;
  }
  if (networkTag === 'BTC') {
    return `bc1q${randomFrom('acdefghjklmnpqrstuvwxyz023456789', 38)}`;
  }
  return `0x${randomFrom('abcdef0123456789', 40)}`;
}

function issueTill(asset: string, networkTag: string): MockTill {
  const catalogue = TILL_ASSETS.find((a) => a.asset === asset);
  const network = catalogue?.networks.find((n) => n.tag === networkTag);

  // The catalogue is the authority on what may be issued. A request for a pair it does not list is
  // refused here rather than tidied into the nearest match, because a till issued on a chain nobody
  // offered is a destination nobody can receive on.
  if (!catalogue || !network) {
    throw new MockRefusal({
      code: 'VALIDATION_FAILED',
      category: 'validation',
      message: `We cannot issue a Till for ${asset} on ${networkTag}.`,
      retryable: false,
    });
  }

  const tills = readTills();
  const existing = tills.find(
    (t) =>
      t.participant_id === DEMO_ID &&
      t.asset === asset &&
      t.network_tag === networkTag &&
      t.status === 'active',
  );
  if (existing) return existing;

  const till: MockTill = {
    id: `till-${randomFrom('abcdefghijklmnopqrstuvwxyz0123456789', 10)}`,
    // THE FORMAT: platform, asset, network, code, run together. See lib/till.ts for why all four
    // are in the handle and why none of them are separated by a character.
    till_number: `LP${asset}${networkTag}${randomFrom(TILL_ALPHABET, 6)}`,
    participant_id: DEMO_ID,
    asset,
    network: network.label,
    network_tag: networkTag,
    // A well-formed but SIMULATED receiving address. It is derived from nothing and controls nothing:
    // the platform's custody is a simulator, and this fixture must not look like a live deposit
    // address that somebody could send real funds to.
    wallet_address: simulatedAddress(networkTag),
    status: 'active',
    created_at: iso(),
    address_is_simulated: true,
  };

  writeTills([till, ...tills]);
  return till;
}

/**
 * Tills belonging to somebody ELSE, so Send Money can be walked end to end.
 *
 * Without one, the only till the app could resolve is the caller's own, and a payment flow that can
 * only pay yourself proves nothing about the thing it is meant to prove: that a till number handed
 * out on a counter resolves, for a stranger, to a named recipient on a named chain.
 *
 * There are two, on two chains, for the same business. That is the case the format exists for: the
 * handles differ visibly, and a payer holding one of them cannot accidentally read it as the other.
 */
const OTHER_TILLS: MockTill[] = [
  {
    id: 'till-kahawa-trc20',
    till_number: 'LPUSDTTRC20KAHAWA',
    participant_id: _COUNTERPARTY_ID,
    asset: 'USDT',
    network: 'Tron',
    network_tag: 'TRC20',
    wallet_address: 'TQ8j4mLp2RfVn6kYcXsA9dHbW3zEuNgKrT',
    status: 'active',
    created_at: iso(-30 * DAY),
    address_is_simulated: true,
  },
  {
    id: 'till-kahawa-btc',
    till_number: 'LPBTCBTCKAHAWA',
    participant_id: _COUNTERPARTY_ID,
    asset: 'BTC',
    network: 'Bitcoin',
    network_tag: 'BTC',
    wallet_address: 'bc1qhjy8f3aw7n2vs6dqmr4xkzc9tpl5ug0e2b8dfa',
    status: 'active',
    created_at: iso(-30 * DAY),
    address_is_simulated: true,
  },
];

const OTHER_TILL_OWNERS: Record<string, string> = {
  LPUSDTTRC20KAHAWA: 'Kahawa House Ltd',
  LPBTCBTCKAHAWA: 'Kahawa House Ltd',
};

// Investor analytics (Investor depth).
const ANALYTICS = {
  netWorth: '407,400.00',
  totalReturn: 21.6,
  yield: 11.4,
  bestPerformer: { name: 'Nyali Apartments', pct: 24.1 },
  worstPerformer: { name: 'SME Invoice Pool', pct: 6.2 },
  performance: [140, 148, 152, 149, 161, 168, 175, 182],
  diversification: [
    { label: 'Real Estate', pct: 42, tone: 'emerald' },
    { label: 'Agriculture', pct: 23, tone: 'lime' },
    { label: 'Energy', pct: 18, tone: 'amber' },
    { label: 'Receivables', pct: 17, tone: 'violet' },
  ],
  cashflow: [
    { month: 'Mar', income: '1,750.00', invested: '30,000.00' },
    { month: 'Apr', income: '2,100.00', invested: '0.00' },
    { month: 'May', income: '2,400.00', invested: '20,000.00' },
    { month: 'Jun', income: '2,400.00', invested: '0.00' },
  ],
};

// Tax statements (Investor depth).
const TAX = {
  summary: { taxableIncome: '12,300.00', capitalGains: '32,400.00', withheld: '1,845.00' },
  years: [
    { year: 2026, income: '12,300.00', gains: '32,400.00', ready: true },
    { year: 2025, income: '4,100.00', gains: '9,800.00', ready: true },
  ],
};

// Connected accounts (Settings depth).
const ACCOUNTS = {
  banks: [{ id: 'b1', name: 'KCB Bank', masked: '...4471', primary: true }],
  mpesa: [
    { id: 'm1', number: '+254 700 000 009', verified: true },
    // Deliberately unverified: the withdraw screen must SHOW it and refuse to use it.
    { id: 'm2', number: '+254 711 000 044', verified: false },
  ],
  wallets: [
    { id: 'w1', network: 'Bitcoin', address: 'bc1q...8f3a', verified: true },
    { id: 'w2', network: 'Ethereum', address: '0x9a...c210', verified: false },
  ],
};

// Credit LENDER portal (Phase 4 second half). Deploy capital against tokenized collateral.
const LENDER = {
  portfolio: {
    deployed: '12,400,000.00',
    outstanding: '9,800,000.00',
    earned: '1,240,000.00',
    activeLoans: 8,
    defaultRate: 1.2,
  },
  applications: [
    {
      id: 'ap1',
      borrower: 'A. Wanjiku',
      amount: '2,000,000.00',
      collateral: 'Nyeri land token',
      ltvPct: 47,
      riskGrade: 'B',
      status: 'pending',
    },
    {
      id: 'ap2',
      borrower: 'J. Mwangi',
      amount: '850,000.00',
      collateral: 'Vehicle token',
      ltvPct: 55,
      riskGrade: 'C',
      status: 'pending',
    },
  ],
  products: [
    {
      id: 'pr1',
      name: 'Asset-backed, 24 months',
      ratePct: 14.5,
      maxLtvPct: 50,
      term: 24,
      status: 'active',
    },
    {
      id: 'pr2',
      name: 'Short-term, 6 months',
      ratePct: 12.0,
      maxLtvPct: 40,
      term: 6,
      status: 'active',
    },
  ],
  loans: [
    {
      id: 'ln-a',
      borrower: 'M. Otieno',
      principal: '3,000,000.00',
      outstanding: '2,100,000.00',
      status: 'performing',
      nextDue: iso(8 * DAY),
    },
    {
      id: 'ln-b',
      borrower: 'S. Achieng',
      principal: '1,500,000.00',
      outstanding: '1,500,000.00',
      status: 'watch',
      nextDue: iso(-2 * DAY),
    },
    {
      id: 'ln-c',
      borrower: 'Zoe Holdings',
      principal: '5,300,000.00',
      outstanding: '4,200,000.00',
      status: 'performing',
      nextDue: iso(20 * DAY),
    },
  ],
};

// Merchant portal (Phase 5). Accept payments, settle to a bank/PSP.
const MERCHANT = {
  profile: { name: 'Zoe Coffee House', till: 'LIPA-7842', status: 'active' },
  today: { received: '18,400.00', count: 23, avg: '800.00' },
  settlement: { pending: '18,400.00', nextAt: iso(1 * DAY), destination: 'KCB Bank ...4471' },
  payments: [
    {
      id: 'p1',
      from: 'Amara W.',
      amount: '450.00',
      method: 'QR',
      at: iso(-1 * HOUR),
      status: 'settled',
    },
    {
      id: 'p2',
      from: 'John K.',
      amount: '1,200.00',
      method: 'link',
      at: iso(-3 * HOUR),
      status: 'settled',
    },
    {
      id: 'p3',
      from: 'Fatuma A.',
      amount: '800.00',
      method: 'QR',
      at: iso(-5 * HOUR),
      status: 'settled',
    },
    {
      id: 'p4',
      from: 'Peter M.',
      amount: '2,500.00',
      method: 'request',
      at: iso(-1 * DAY),
      status: 'settled',
    },
  ],
  links: [
    {
      id: 'l1',
      title: 'Monthly coffee subscription',
      amount: '2,000.00',
      uses: 14,
      status: 'active',
    },
    { id: 'l2', title: 'Event catering deposit', amount: '15,000.00', uses: 3, status: 'active' },
  ],
};

// Business portal (Phase 5). Tier 3 KYB: the company and its people.
const BUSINESS = {
  profile: {
    legal_name: 'Zoe Empire Holdings Ltd',
    reg: 'PVT-CX-2026-8841',
    kyb_status: 'verified',
    tier: 'T3',
    wallet: '2,400,000.00',
  },
  directors: [
    { name: 'Wanjiru Kamau', role: 'Director', id_ref: '**** 4471', screening: 'clear' },
    { name: 'David Otieno', role: 'Director', id_ref: '**** 9920', screening: 'clear' },
  ],
  beneficialOwners: [
    { name: 'Wanjiru Kamau', pct: 55, screening: 'clear' },
    { name: 'Zoe Ventures LP', pct: 30, screening: 'clear' },
    { name: 'David Otieno', pct: 15, screening: 'clear' },
  ],
  signatories: [
    { name: 'Wanjiru Kamau', role: 'Primary', permissions: ['Payments', 'Settlement', 'Team'] },
    { name: 'Grace Njeri', role: 'Finance', permissions: ['Payments'] },
  ],
};

// Credit & collateral (Phase 4). Borrow against a tokenized asset: the token is pledged and locked.
// ---------------------------------------------------------------------------
// ASSET FINANCE MARKETPLACE (borrower side of the two-sided credit marketplace).
//
// The reframe: credit revolves around ASSETS, not loans. A sponsor borrows because they own a verified,
// trust-administered asset, not because they hold an account. So the model leads with borrowing power and
// eligible assets, then products, then a multi-DCP request, then competing offers, then the loan.
// ---------------------------------------------------------------------------

const FINANCE_PRODUCTS = [
  {
    id: 'fp-mortgage',
    name: 'Commercial mortgage',
    category: 'Real estate',
    provider: 'Green Capital',
    providerRating: 4.6,
    interestPct: 11.5,
    maxLtvPct: 65,
    tenureMonths: 120,
    minValue: '5,000,000.00',
    maxAmount: '80,000,000.00',
    repayment: 'Monthly',
    supportedAssets: ['Property'],
    approvalDays: 5,
    docs: ['Financial statements', 'Rental income', 'Insurance', 'Tax clearance'],
  },
  {
    id: 'fp-bridge',
    name: 'Bridge finance',
    category: 'Bridge',
    provider: 'ABC Finance',
    providerRating: 4.2,
    interestPct: 14.0,
    maxLtvPct: 55,
    tenureMonths: 12,
    minValue: '3,000,000.00',
    maxAmount: '40,000,000.00',
    repayment: 'On exit',
    supportedAssets: ['Property', 'Land'],
    approvalDays: 2,
    docs: ['Valuation', 'Exit plan'],
  },
  {
    id: 'fp-rental',
    name: 'Rental advance',
    category: 'Real estate',
    provider: 'Property Finance Ltd',
    providerRating: 4.4,
    interestPct: 12.0,
    maxLtvPct: 40,
    tenureMonths: 24,
    minValue: '2,000,000.00',
    maxAmount: '20,000,000.00',
    repayment: 'Monthly',
    supportedAssets: ['Property'],
    approvalDays: 3,
    docs: ['Lease agreements', 'Rental income'],
  },
  {
    id: 'fp-working',
    name: 'Working capital',
    category: 'Working capital',
    provider: 'Equity Finance',
    providerRating: 4.7,
    interestPct: 13.5,
    maxLtvPct: 50,
    tenureMonths: 18,
    minValue: '1,000,000.00',
    maxAmount: '15,000,000.00',
    repayment: 'Monthly',
    supportedAssets: ['Business', 'Receivable'],
    approvalDays: 4,
    docs: ['Financial statements', 'Cash flow'],
  },
  {
    id: 'fp-vehicle',
    name: 'Vehicle equity loan',
    category: 'Vehicles',
    provider: 'ABC Finance',
    providerRating: 4.2,
    interestPct: 15.5,
    maxLtvPct: 60,
    tenureMonths: 36,
    minValue: '500,000.00',
    maxAmount: '10,000,000.00',
    repayment: 'Monthly',
    supportedAssets: ['Vehicle'],
    approvalDays: 2,
    docs: ['Logbook', 'Insurance'],
  },
  {
    id: 'fp-crop',
    name: 'Seasonal crop finance',
    category: 'Agriculture',
    provider: 'Green Credit',
    providerRating: 4.3,
    interestPct: 12.8,
    maxLtvPct: 45,
    tenureMonths: 9,
    minValue: '300,000.00',
    maxAmount: '8,000,000.00',
    repayment: 'On harvest',
    supportedAssets: ['Agriculture'],
    approvalDays: 5,
    docs: ['Farm plan', 'Input budget'],
  },
  {
    id: 'fp-warehouse',
    name: 'Warehouse receipt finance',
    category: 'Commodities',
    provider: 'Green Capital',
    providerRating: 4.6,
    interestPct: 11.0,
    maxLtvPct: 70,
    tenureMonths: 6,
    minValue: '1,000,000.00',
    maxAmount: '25,000,000.00',
    repayment: 'On exit',
    supportedAssets: ['Commodity'],
    approvalDays: 3,
    docs: ['Warehouse receipt', 'Grading report'],
  },
  {
    id: 'fp-invoice',
    name: 'Invoice discounting',
    category: 'Receivables',
    provider: 'Equity Finance',
    providerRating: 4.7,
    interestPct: 13.0,
    maxLtvPct: 80,
    tenureMonths: 4,
    minValue: '250,000.00',
    maxAmount: '12,000,000.00',
    repayment: 'On settlement',
    supportedAssets: ['Receivable'],
    approvalDays: 1,
    docs: ['Invoices', 'Debtor confirmation'],
  },
  {
    id: 'fp-solar',
    name: 'Solar asset finance',
    category: 'Renewable energy',
    provider: 'Green Credit',
    providerRating: 4.3,
    interestPct: 12.5,
    maxLtvPct: 60,
    tenureMonths: 60,
    minValue: '2,000,000.00',
    maxAmount: '30,000,000.00',
    repayment: 'Monthly',
    supportedAssets: ['Energy Project', 'Equipment'],
    approvalDays: 6,
    docs: ['Project plan', 'Offtake agreement'],
  },
  {
    id: 'fp-construction',
    name: 'Construction finance',
    category: 'Development',
    provider: 'Property Finance Ltd',
    providerRating: 4.4,
    interestPct: 13.8,
    maxLtvPct: 55,
    tenureMonths: 24,
    minValue: '10,000,000.00',
    maxAmount: '120,000,000.00',
    repayment: 'Drawdown',
    supportedAssets: ['Property', 'Land'],
    approvalDays: 7,
    docs: ['Construction budget', 'Permits', 'Contractor profile'],
  },
];

// The sponsor's tokenized assets, as financing opportunities. Reuses the sponsor issuance fixtures.
const ELIGIBLE_ASSETS = [
  {
    id: 'sa-green-valley',
    name: 'Green Valley Apartments',
    type: 'Property',
    trustStatus: 'active',
    valuation: '95,000,000.00',
    tokenizedValue: '95,000,000.00',
    currentLtvPct: 21,
    maxBorrowing: '61,750,000.00',
    existingLoan: '20,000,000.00',
    availableEquity: '41,750,000.00',
    maxLtvPct: 65,
    eligibleProducts: ['fp-mortgage', 'fp-bridge', 'fp-rental', 'fp-construction'],
    interestedDcps: 4,
  },
  {
    id: 'sa-boda',
    name: 'Boda Fleet Financing',
    type: 'Vehicle',
    trustStatus: 'forming',
    valuation: '8,000,000.00',
    tokenizedValue: '0.00',
    currentLtvPct: 0,
    maxBorrowing: '4,800,000.00',
    existingLoan: '0.00',
    availableEquity: '4,800,000.00',
    maxLtvPct: 60,
    eligibleProducts: ['fp-vehicle', 'fp-working'],
    interestedDcps: 2,
  },
];

const FINANCE = {
  borrowingPower: {
    portfolioValue: '103,000,000.00',
    pledgedValue: '20,000,000.00',
    availableEquity: '46,550,000.00',
    maxCapacity: '66,550,000.00',
    utilizationPct: 30,
    eligibleProducts: FINANCE_PRODUCTS.length,
    interestedDcps: 6,
    activeLoans: 1,
    repaymentDue: '120,000.00',
    nextDue: iso(14 * DAY),
    openOffers: 3,
    avgInterestPct: 11.2,
  },
  eligibleAssets: ELIGIBLE_ASSETS,
  products: FINANCE_PRODUCTS,
  applications: [
    {
      id: 'app-1',
      asset: 'Green Valley Apartments',
      assetId: 'sa-green-valley',
      product: 'Commercial mortgage',
      amount: '35,000,000.00',
      status: 'raising',
      dcps: ['Green Capital', 'ABC Finance', 'Property Finance Ltd'],
      createdAt: iso(-6 * DAY),
      offers: 3,
    },
  ],
  offers: [
    {
      id: 'of-1',
      applicationId: 'app-1',
      asset: 'Green Valley Apartments',
      provider: 'Green Capital',
      providerRating: 4.6,
      interestPct: 11.0,
      aprPct: 11.9,
      maxLtvPct: 60,
      tenureMonths: 120,
      amount: '35,000,000.00',
      fees: '350,000.00',
      insurance: 'Required',
      repayment: 'Monthly',
      earlySettlement: 'Allowed, no penalty',
      conditions: ['Valuation within 90 days', 'Building insurance assigned'],
      status: 'indicative',
    },
    {
      id: 'of-2',
      applicationId: 'app-1',
      asset: 'Green Valley Apartments',
      provider: 'ABC Finance',
      providerRating: 4.2,
      interestPct: 9.8,
      aprPct: 10.6,
      maxLtvPct: 55,
      tenureMonths: 96,
      amount: '32,000,000.00',
      fees: '480,000.00',
      insurance: 'Required',
      repayment: 'Monthly',
      earlySettlement: '2% penalty in year 1',
      conditions: ['Site visit required', 'Sponsor equity 45% minimum'],
      status: 'indicative',
    },
    {
      id: 'of-3',
      applicationId: 'app-1',
      asset: 'Green Valley Apartments',
      provider: 'Property Finance Ltd',
      providerRating: 4.4,
      interestPct: 12.0,
      aprPct: 12.7,
      maxLtvPct: 70,
      tenureMonths: 144,
      amount: '38,000,000.00',
      fees: '300,000.00',
      insurance: 'Required',
      repayment: 'Monthly',
      earlySettlement: 'Allowed, no penalty',
      conditions: ['Rates clearance', 'Occupancy above 85%'],
      status: 'conditional',
    },
  ],
  loans: [
    {
      id: 'ln-1',
      product: 'Commercial mortgage',
      provider: 'Green Capital',
      asset: 'Green Valley Apartments',
      assetId: 'sa-green-valley',
      principal: '20,000,000.00',
      outstanding: '18,400,000.00',
      ratePct: 11.5,
      termMonths: 120,
      monthly: '120,000.00',
      nextDue: iso(14 * DAY),
      status: 'performing',
      collateral: 'Green Valley token allocation',
      paidPct: 8,
      disbursedAt: iso(-90 * DAY),
    },
  ],
  collateral: [
    {
      asset: 'Green Valley Apartments',
      assetId: 'sa-green-valley',
      provider: 'Green Capital',
      loanId: 'ln-1',
      tokensLocked: 200000,
      ltvPct: 21,
      outstanding: '18,400,000.00',
      releaseOn: 'Loan discharge',
      status: 'locked',
      priority: 1,
    },
  ],
};

function financeOffer(id: string) {
  return FINANCE.offers.find((o) => o.id === id) ?? FINANCE.offers[0];
}

function financeLoanDetail(id: string) {
  const loan = FINANCE.loans.find((l) => l.id === id) ?? FINANCE.loans[0];
  const schedule = Array.from({ length: 10 }).map((_, i) => ({
    n: i + 1,
    due: iso((i - 2) * 30 * DAY),
    amount: loan.monthly,
    status: i < 2 ? 'paid' : i === 2 ? 'due' : 'upcoming',
  }));
  const timeline = [
    { key: 'application', label: 'Application', at: iso(-110 * DAY), done: true },
    { key: 'offer', label: 'Offer accepted', at: iso(-100 * DAY), done: true },
    { key: 'approval', label: 'Approval', at: iso(-95 * DAY), done: true },
    { key: 'collateral', label: 'Collateral registered', at: iso(-92 * DAY), done: true },
    { key: 'disbursement', label: 'Disbursement', at: iso(-90 * DAY), done: true },
    { key: 'repayment', label: 'Repayment', at: iso(-60 * DAY), done: true },
    { key: 'closure', label: 'Closure', at: null, done: false },
  ];
  const documents = [
    'Loan agreement',
    'Security agreement',
    'Insurance certificate',
    'Valuation report',
    'Repayment schedule',
  ];
  return { ...loan, schedule, timeline, documents };
}

// The Asset Financial Passport: the persistent record that ties tokenization, trust, marketplace, and
// credit together, so a sponsor never re-applies with documents. Assembled from the sponsor + finance data.
function assetPassport(assetId: string) {
  const sponsor = SPONSOR_ASSETS.find((a) => a.id === assetId);
  const eligible = ELIGIBLE_ASSETS.find((a) => a.id === assetId);
  const loans = FINANCE.loans.filter((l) => l.assetId === assetId);
  const collateral = FINANCE.collateral.filter((c) => c.assetId === assetId);
  const base = sponsor ?? SPONSOR_ASSETS[0];
  return {
    id: base.id,
    name: base.name,
    type: base.type,
    location: base.location,
    trust: base.trust,
    valuationHistory: [
      {
        value: base.valuation?.approved ?? '95,000,000.00',
        at: base.valuation?.date ?? iso(-90 * DAY),
        valuer: base.valuation?.valuer ?? 'Knight Frank Kenya',
      },
      { value: base.estimatedValue, at: iso(-120 * DAY), valuer: 'Sponsor estimate' },
    ],
    supply: base.capital
      ? { total: base.capital.totalSupply, price: base.capital.tokenPrice }
      : null,
    treasury: base.treasury,
    listings: base.offerings.length,
    capitalRaised: base.capitalRaised,
    financing: {
      loans: loans.length,
      outstanding: loans[0]?.outstanding ?? '0.00',
      maxBorrowing: eligible?.maxBorrowing ?? '0.00',
      availableEquity: eligible?.availableEquity ?? '0.00',
    },
    collateral,
    insurance: 'Active, expires ' + new Date().getFullYear() + '',
    compliance: ['KYB verified', 'Trust formation complete', 'Screening clear'],
  };
}

// Tokenization Hub + Trust Entity (Phase 3).
function assetStatus(assetId: string) {
  const asset = MY_ASSETS.find((a) => a.asset_id === assetId) ?? MY_ASSETS[1];
  const done = asset.verification_status === 'tokenized';
  const isVehicle = asset.class === 'vehicle';
  return {
    asset_id: assetId,
    class: asset.class,
    ownership_reference: asset.ownership_reference,
    verification_status: asset.verification_status,
    timeline: {
      legal_entity_holds_it: isVehicle ? null : done,
      ownership_proof_provided: true,
      land_register_searched: isVehicle ? null : done,
      land_register_agreed: isVehicle ? null : done,
      valuation_confirmed: done,
      token_issued: done,
    },
    ready_to_mint: done,
    blocking: done ? [] : ['Valuation pending', 'Ownership verification in progress'],
    token_id: asset.token_id,
  };
}

const TOKEN_DETAIL = {
  token_id: 'tok-1',
  classification: 'asset_backed',
  state: 'issued',
  minted_at: iso(-20 * DAY),
  asset: { id: 'a1', class: 'land', ownership_reference: 'LR/NRB/BLOCK-12/PLOT-4471' },
  legal_entity: {
    id: 'ent-1',
    legal_name: 'Block 12 Plot 4471 Trust',
    kind: 'trust',
    jurisdiction: 'Kenya',
    registration_number: 'KE-TR-2026-0041',
  },
  what_you_own:
    'You own a 100% beneficial interest in Block 12 Plot 4471 Trust, the legal entity that holds the land at LR/NRB/BLOCK-12/PLOT-4471.',
  current_valuation: {
    amount: '850000000',
    asset_code: 'KES',
    method: 'Professional appraisal',
    effective_from: iso(-30 * DAY),
  },
  title_verified_against: {
    registry: 'Ministry of Lands',
    registered_owner: 'Block 12 Plot 4471 Trust',
    checked_at: iso(-25 * DAY),
  },
  holders: [{ holder_id: DEMO_ID, fraction_bps: 10000 }],
};

const HOLDINGS = [
  {
    token_id: 'tok-1',
    fraction_bps: 10000,
    classification: 'asset_backed',
    state: 'issued',
    asset_class: 'land',
    ownership_reference: 'LR/NRB/BLOCK-12/PLOT-4471',
  },
];

const TRUSTS = [
  {
    id: 'ent-1',
    legal_name: 'Block 12 Plot 4471 Trust',
    kind: 'trust',
    jurisdiction: 'Kenya',
    registration_number: 'KE-TR-2026-0041',
    status: 'active',
    asset: 'Land at LR/NRB/BLOCK-12/PLOT-4471',
    asset_class: 'land',
    trustee: 'Zoe Fiduciary Services Ltd',
    beneficiaries: 1,
    holders: 1,
    valuation: '8,500,000.00',
    created_at: iso(-30 * DAY),
    distributions: [
      { kind: 'Rental income', amount: '120,000.00', at: iso(-15 * DAY) },
      { kind: 'Rental income', amount: '118,000.00', at: iso(-45 * DAY) },
    ],
  },
];

// Tracks which contact channels have been verified during a mock onboarding walk, so verifying the
// phone does not also claim the email is done. Module-level, which is fine for a single-session demo.
const CONTACT: { phone: boolean; email: boolean } = { phone: false, email: false };

type Handler = (m: RegExpMatchArray, query: URLSearchParams, init?: RequestInit) => unknown;

/** [method, path-regex, handler]. First match wins. Query string is stripped before matching. */
/**
 * ASSISTED HELP (FR-U-DSP-006).
 *
 * The fixture mirrors the REAL corpus published by migration 0029, including the handoff, because the
 * handoff is the behaviour most worth designing against: a question our articles do not cover must
 * produce a route to a human, not an apology and not a guess. A mock that always answers would let us
 * ship a drawer that has never rendered its most important state.
 */
const HELP_PASSAGES: { match: RegExp; source_id: string; title: string; quote: string }[] = [
  {
    match: /block|declin|fail|reject|limit|cap/i,
    source_id: 'why-a-payment-was-blocked@v1.0',
    title: 'Why a payment did not go through',
    quote:
      'The most common reasons are a limit and a verification level. There is a cap on a single transaction and on how much can move in a day, and the app tells you the amount and when the limit resets. Some actions also need a higher verification level than your account currently has.',
  },
  {
    match: /dispute|complain|report|wrong|stole|someone|somebody/i,
    source_id: 'raising-a-dispute@v1.0',
    title: 'Raising a dispute',
    quote:
      'Some kinds of report are treated as urgent automatically. If you report that somebody else has used your account, we look at it within 4 hours. If your money is stuck in escrow, or your account is frozen, we look at it within 12 hours. You do not have to ask for it to be treated as urgent.',
  },
  {
    match: /escrow|held|stuck|pending/i,
    source_id: 'escrow@v1.0',
    title: 'Why money is held in escrow',
    quote:
      'If you raise a dispute about a payment, the escrow stays held while the dispute runs. It is not released to the other side, and it is not returned to you, until the case is decided. That is deliberate: it protects whichever of you turns out to be right.',
  },
  {
    match: /token|own|asset|property|land/i,
    source_id: 'what-you-own-when-you-hold-a-token@v1.0',
    title: 'What you own when you hold a token',
    quote:
      'A token does not make you the owner of the land, the building, or the item itself. It represents an interest in a legal entity that holds that asset. This distinction is not a technicality: it is what your rights actually are.',
  },
  {
    match: /tier|verif|kyc|level|upgrade/i,
    source_id: 'verification-tiers@v1.0',
    title: 'Verification levels',
    quote:
      'Your account has a verification level, and it decides what you can do and how much you can move. A new account starts at the lowest level and can only look around.',
  },
];

const ASSISTED_HELP_DISCLOSURE = {
  type: 'assisted_help',
  version: 'v1.0',
  title: 'Assisted help',
  body:
    'Assisted help answers questions using our published help articles. It is optional and you ' +
    'can use the app fully without it. To answer you, the words you type are sent to a service ' +
    'provider outside Kenya, so please do not type your PIN, a password, or a full identity ' +
    'document number into it. It cannot see your balance, your transactions, or your cases, and ' +
    'it cannot move money or change anything on your account. It can be wrong. Anything it tells ' +
    'you that matters should be confirmed with our support team, and it will hand you over to a ' +
    'person whenever it is unsure. We keep a record of these conversations so that our support ' +
    'and dispute teams can see what you were told.',
};

/** Flipped by the mock consent route, so the opt-in gate is walkable rather than assumed. */
let mockAssistantConsent = false;

function mockAssistantAnswer(init?: RequestInit) {
  const body = JSON.parse(String(init?.body ?? '{}')) as {
    question?: string;
    conversation_id?: string | null;
  };
  const question = body.question ?? '';
  const hit = HELP_PASSAGES.find((p) => p.match.test(question));
  const conversation_id = body.conversation_id ?? 'mock-conversation-0001';

  if (!hit) {
    return {
      conversation_id,
      body:
        'I could not find anything in our help articles that answers that. Our support team ' +
        'can help, and you can reach them from the support screen. If money is involved, ' +
        'raising a case there is the fastest route.',
      citations: [],
      handed_off: true,
    };
  }

  return {
    conversation_id,
    body: hit.quote,
    citations: [{ source_id: hit.source_id, title: hit.title, quote: hit.quote }],
    handed_off: false,
  };
}

const ROUTES: [string, RegExp, Handler][] = [
  // FIRST, deliberately. First match wins, and the credit marketplace shares path shapes with the
  // older asset-finance fixtures; a stale route that shadowed `/credit/loans/:id` is exactly how the
  // new loan screen came to render the old fixture's data.
  ...creditRoutes(),
  [
    'GET',
    /^\/assistant\/availability$/,
    () => ({
      // TRUE here on purpose, unlike the real default. The backend ships with the simulator so the
      // surface is inert; the mock exists to design against the surface that is switched ON.
      enabled: true,
      consented: mockAssistantConsent,
      disclosure: ASSISTED_HELP_DISCLOSURE,
    }),
  ],
  ['POST', /^\/assistant\/ask$/, (_m, _q, init) => mockAssistantAnswer(init)],
  ['POST', /^\/identity\/auth\/login$/, () => ({ token: 'mock-token', participant_id: DEMO_ID })],
  [
    'POST',
    /^\/identity\/auth\/enrol-pin$/,
    () => ({ token: 'mock-token', participant_id: DEMO_ID }),
  ],
  ['GET', /^\/identity\/auth\/me$/, () => ME],
  ['GET', /^\/identity\/auth\/sessions$/, () => SESSIONS],
  ['POST', /^\/identity\/auth\/logout$/, () => ({ signed_out: true })],
  ['POST', /^\/identity\/auth\/logout-all$/, () => ({ sessions_ended: 2 })],
  ['POST', /^\/identity\/auth\/sessions\/[^/]+\/revoke$/, () => ({ revoked: true })],
  ['GET', /^\/identity\/[^/]+\/disclosures$/, () => DISCLOSURES],
  [
    'POST',
    /^\/identity\/[^/]+\/consent$/,
    (_m, _q, init) => {
      const body = JSON.parse(String(init?.body ?? '{}')) as { consent_type?: string };
      if (body.consent_type === 'assisted_help') mockAssistantConsent = true;
      return { recorded: true, version: 'v1.0' };
    },
  ],
  [
    'GET',
    /^\/identity\/[^/]+$/,
    () => ({ id: DEMO_ID, type: 'individual', tier: 'T2', status: 'active', whitelisted: true }),
  ],
  ['POST', /^\/identity\/[^/]+\/consent$/, () => ({ recorded: true })],

  // ONBOARDING WALK. Register MUST return {id, tier, status, enrolment_token}: the Set-PIN step submits
  // enrolment_token, and if it is missing the button silently no-ops (this was the reported bug). The
  // contact + KYC routes below let the whole flow complete end to end in the demo.
  [
    'POST',
    /^\/identity\/register$/,
    () => ({ id: DEMO_ID, tier: 'T0', status: 'unverified', enrolment_token: 'mock-enrol' }),
  ],
  [
    'POST',
    /^\/identity\/[^/]+\/contact\/(phone|email)\/request$/,
    (m) => ({ channel: m[1], expires_at: iso(10 * 60_000), dev_code: '123456' }),
  ],
  [
    'POST',
    /^\/identity\/[^/]+\/contact\/(phone|email)\/verify$/,
    (m) => {
      CONTACT[m[1] as 'phone' | 'email'] = true;
      return { phone_verified: CONTACT.phone, email_verified: CONTACT.email };
    },
  ],
  [
    'GET',
    /^\/identity\/[^/]+\/contact$/,
    () => ({ phone_verified: CONTACT.phone, email_verified: CONTACT.email }),
  ],
  [
    'POST',
    /^\/identity\/[^/]+\/documents$/,
    () => ({
      document_id: 'doc-' + Math.floor(Math.random() * 1e9).toString(36),
      content_hash: 'sha256:mock',
      doc_type: 'national_id',
    }),
  ],
  [
    'POST',
    /^\/identity\/[^/]+\/verify\/tier1$/,
    () => ({ tier: 'T1', status: 'active', whitelisted: true, risk_band: 'low' }),
  ],
  [
    'POST',
    /^\/identity\/[^/]+\/verify\/tier2$/,
    () => ({ tier: 'T2', status: 'active', risk_band: 'low' }),
  ],

  ['GET', /^\/wallet\/[^/]+\/locks$/, () => ({ locks: [] })],
  ['GET', /^\/wallet\/[^/]+\/history$/, () => HISTORY],
  ['POST', /^\/wallet\/[^/]+\/dev-topup$/, () => WALLET],
  ['GET', /^\/wallet\/[^/]+$/, () => WALLET],
  ['GET', /^\/limits\/[^/]+$/, () => LIMITS],

  [
    'GET',
    /^\/notifications\/unread-count$/,
    () => ({ unread: NOTIFICATIONS.filter((n) => !n.read).length }),
  ],
  ['POST', /^\/notifications\/read-all$/, () => ({ read: true })],
  ['POST', /^\/notifications\/[^/]+\/read$/, () => ({ read: true })],
  ['POST', /^\/notifications\/[^/]+\/acknowledge$/, () => ({ acknowledged: true })],
  ['GET', /^\/notifications$/, () => NOTIFICATIONS],

  ['GET', /^\/rates\/quote$/, (_m, query) => computeQuote(query)],
  [
    'GET',
    /^\/rates$/,
    () => ({
      numeraire: 'KES',
      source: 'simulator',
      as_of: iso(),
      live: false,
      fee_bps: 50,
      rates: Object.entries(RATES).map(([asset, v]) => ({ asset, kes_per_unit: String(v) })),
    }),
  ],

  [
    'POST',
    /^\/transact\/send$/,
    () => ({
      // Snake case, matching what the real backend sends. These used to be camelCase and agreed
      // with a TS type that was wrong, which is why the mismatch survived: mock mode confirmed the
      // bug instead of exposing it.
      outcome: 'settled',
      transaction_id: 'txn-mock-1',
      escrow_id: 'esc-mock-1',
      correlation_id: 'corr-mock-1',
    }),
  ],
  ['GET', /^\/transact\/[^/]+$/, (m) => ({ correlation_id: m[0].split('/').pop(), events: [] })],

  ['GET', /^\/disputes$/, () => DISPUTES],
  ['GET', /^\/disputes\/[^/]+$/, () => DISPUTES[0]],
  [
    'POST',
    /^\/disputes$/,
    () => ({ ...DISPUTES[0], case_id: 'D-2026-0043', status: 'open', opened_at: iso() }),
  ],
  ['POST', /^\/disputes\/[^/]+\/withdraw$/, () => ({ ...DISPUTES[0], status: 'withdrawn' })],

  ['GET', /^\/tokenize\/my-assets$/, () => MY_ASSETS],
  ['GET', /^\/tokenize\/assets\/[^/]+$/, (m) => assetStatus(m[0].split('/').pop() as string)],
  [
    'POST',
    /^\/tokenize\/assets\/[^/]+\/documents$/,
    () => ({
      document_id: 'doc-' + Math.floor(Math.random() * 1e9).toString(36),
      content_hash: 'sha256:mock',
    }),
  ],
  [
    'POST',
    /^\/tokenize\/assets$/,
    () => ({ asset_id: 'a-' + Math.floor(Math.random() * 1e9).toString(36) }),
  ],
  ['GET', /^\/tokenize\/tokens\/[^/]+$/, () => TOKEN_DETAIL],
  ['GET', /^\/tokenize\/holdings\/[^/]+$/, () => HOLDINGS],
  [
    'POST',
    /^\/tokenize\/entities$/,
    () => ({ entity_id: 'ent-' + Math.floor(Math.random() * 1e9).toString(36) }),
  ],
  // Sponsor issuance (economic-vs-legal split). The sponsor submits, approves capital, requests offerings.
  ['GET', /^\/sponsor\/summary$/, () => sponsorSummary()],
  ['GET', /^\/sponsor\/offerings$/, () => sponsorOfferings()],
  ['GET', /^\/sponsor\/distributions$/, () => SPONSOR_DISTRIBUTIONS],
  ['GET', /^\/sponsor\/assets$/, () => ({ assets: SPONSOR_ASSETS })],
  [
    'GET',
    /^\/sponsor\/assets\/[^/]+$/,
    (m) => SPONSOR_ASSETS.find((a) => a.id === m[0].split('/').pop()) ?? SPONSOR_ASSETS[0],
  ],
  ['POST', /^\/sponsor\/assets\/[^/]+\/capital\/approve$/, () => ({ ok: true })],
  [
    'POST',
    /^\/sponsor\/assets\/[^/]+\/offerings$/,
    () => ({ id: 'off-' + Math.floor(Math.random() * 1e9).toString(36) }),
  ],
  [
    'POST',
    /^\/sponsor\/assets$/,
    () => ({ id: 'sa-' + Math.floor(Math.random() * 1e9).toString(36) }),
  ],
  // Trust Entity management (Phase 3, new surface).
  ['GET', /^\/trust$/, () => ({ trusts: TRUSTS })],
  [
    'GET',
    /^\/trust\/[^/]+$/,
    (m) => TRUSTS.find((t) => t.id === m[0].split('/').pop()) ?? TRUSTS[0],
  ],
  ['POST', /^\/trust$/, () => ({ id: 'ent-' + Math.floor(Math.random() * 1e9).toString(36) })],

  [
    'GET',
    /^\/marketplace\/listings\/[^/]+$/,
    (m) => LISTINGS.find((l) => l.id === m[0].split('/').pop()) ?? LISTINGS[0],
  ],
  [
    'GET',
    /^\/marketplace\/listings$/,
    () => ({ categories: MARKET_CATEGORIES, listings: LISTINGS }),
  ],
  ['POST', /^\/marketplace\/listings\/[^/]+\/invest$/, () => ({ ok: true })],
  ['GET', /^\/portfolio$/, () => PORTFOLIO],
  ['GET', /^\/earnings$/, () => EARNINGS],

  // ---------------------------------------------------------------------------------------------
  // FIAT OFF RAMP (FR-U-WAL-006). Withdraw to a bank account or M-Pesa.
  //
  // The refusal below is the point of this fixture, not an edge case. The seeded per transaction cap
  // is KES 50,000, and a withdraw screen that always succeeds is one where nobody ever sees the cap
  // copy, the alert styling or the "nothing has left your balance" reassurance until a real backend
  // says no. The wording is the backend's own disclosable_reason, copied, so the two do not drift.
  // ---------------------------------------------------------------------------------------------
  [
    'POST',
    /^\/wallet\/withdrawals$/,
    (_m, _q, init) => {
      const body = JSON.parse(String(init?.body ?? '{}')) as {
        amount?: string;
        rail?: string;
        destination_ref?: string;
      };

      // EXACT, via the shared money utility. `Math.round(Number(amount) * 100)` was here, and it is
      // the same class of mistake as the comma above: a fixture doing money arithmetic through an
      // IEEE-754 double while the platform it stands in for refuses to. It also threw NaN at the cap
      // comparison for any amount the parser did not like, which reads as "allowed".
      let minor: bigint;
      try {
        minor = toBaseUnits(body.amount ?? '');
      } catch {
        throw new MockRefusal({
          code: 'VALIDATION_FAILED',
          category: 'validation',
          message: 'Enter a valid withdrawal amount.',
          retryable: false,
        });
      }

      if (minor <= 0n) {
        throw new MockRefusal({
          code: 'VALIDATION_FAILED',
          category: 'validation',
          message: 'Enter a withdrawal amount above zero.',
          retryable: false,
        });
      }

      if (minor > 5_000_000n) {
        throw new MockRefusal({
          code: 'CAP_EXCEEDED',
          category: 'policy',
          message:
            'This withdrawal would exceed your single transaction limit. Nothing has left your ' +
            'balance.',
          // A policy refusal is never retryable: retrying a cap breach is an attempt to get around it.
          retryable: false,
        });
      }

      MOCK_RAMPS.unshift({
        instruction_id: `ri-${minor}-${MOCK_RAMPS.length + 1}`,
        direction: 'withdrawal',
        rail: body.rail ?? 'mobile_money',
        amount: fromBaseUnits(minor),
        asset: 'KES',
        state: 'instructed',
        provider_reference: null,
        failure_reason: null,
        created_at: iso(),
        confirmed_at: null,
      });

      return {
        instruction_id: MOCK_RAMPS[0].instruction_id,
        state: 'instructed',
        amount: MOCK_RAMPS[0].amount,
        correlation_id: `cor-${MOCK_RAMPS[0].instruction_id}`,
        // Verbatim from platform/ramps.py. Held is not paid, and the participant is told which.
        note:
          'The amount is held and is no longer spendable. It leaves your balance when the rail ' +
          'confirms the payout, and returns to it if the rail refuses.',
      };
    },
  ],
  ['GET', /^\/wallet\/ramps$/, () => MOCK_RAMPS],

  ['POST', /^\/earnings\/withdraw$/, () => ({ ok: true })],
  ['GET', /^\/market$/, () => ({ assets: MARKET })],
  [
    'POST',
    /^\/market\/[^/]+\/order$/,
    () => ({ ok: true, order_id: 'ord-' + Math.floor(Math.random() * 1e9).toString(36) }),
  ],

  // Asset Finance Marketplace (borrower side of the two-sided credit marketplace).
  ['GET', /^\/finance\/overview$/, () => FINANCE],
  ['GET', /^\/finance\/products$/, () => ({ products: FINANCE.products })],
  ['GET', /^\/finance\/assets$/, () => ({ assets: FINANCE.eligibleAssets })],
  ['GET', /^\/finance\/offers$/, () => ({ offers: FINANCE.offers })],
  ['GET', /^\/finance\/offers\/[^/]+$/, (m) => financeOffer(m[0].split('/').pop() as string)],
  ['POST', /^\/finance\/offers\/[^/]+\/(accept|decline)$/, () => ({ ok: true })],
  ['GET', /^\/finance\/loans$/, () => ({ loans: FINANCE.loans })],
  ['GET', /^\/finance\/loans\/[^/]+$/, (m) => financeLoanDetail(m[0].split('/').pop() as string)],
  ['GET', /^\/finance\/collateral$/, () => ({ collateral: FINANCE.collateral })],
  ['GET', /^\/finance\/passport\/[^/]+$/, (m) => assetPassport(m[0].split('/').pop() as string)],
  [
    'POST',
    /^\/finance\/requests$/,
    () => ({ id: 'app-' + Math.floor(Math.random() * 1e9).toString(36) }),
  ],
  ['POST', /^\/finance\/loans\/[^/]+\/repay$/, () => ({ ok: true })],

  [
    'GET',
    /^\/merchant\/settlements\/quote/,
    () => ({
      asset: 'KES',
      payment_count: 12,
      gross: '48250.00',
      standard: { fee: '0.00', net: '48250.00', arrives: 'when the acquiring cycle completes' },
      // Priced, and unavailable, which is the state worth showing in a mock: the panel has to
      // explain a refusal rather than hide the card.
      instant: { fee: '241.25', fee_bps: 50, net: '48008.75', arrives: 'now' },
      instant_available: false,
      instant_refusal: 'exposure_cap',
    }),
  ],
  ['GET', /^\/merchant\/settlements\/advances$/, () => []],
  [
    'POST',
    /^\/merchant\/settlements$/,
    () => ({
      settlement_id: 'stl-mock',
      mode: 'standard',
      status: 'pending',
      note: 'Sent to the settlement rail. It is not paid until the rail confirms it.',
      gross: '48250.00',
      fee: '0.00',
      net: '48250.00',
    }),
  ],
  ['GET', /^\/p2p\/payment-methods$/, () => P2P_METHODS],
  ['GET', /^\/p2p\/offers$/, (_m, query) => p2pBook(query.get('side') ?? 'buy')],
  [
    'POST',
    /^\/p2p\/offers$/,
    (_m, _q, init) => createMockOffer(JSON.parse(String(init?.body ?? '{}'))),
  ],
  [
    'POST',
    /^\/p2p\/offers\/[^/]+\/take$/,
    (m) => ({
      outcome: 'started',
      trade_id: 't-' + m[0].split('/')[3],
      release_path: 'counterparty_confirmed',
      settles_automatically: false,
      escrow_id: 'esc-mock',
      correlation_id: 'corr-mock',
    }),
  ],
  [
    'GET',
    /^\/p2p\/trades$/,
    () => ({
      trades: [
        {
          id: 't-1',
          state: 'awaiting_payment',
          asset: 'USDT',
          amount: '250.000000',
          release_path: 'counterparty_confirmed',
          settles_automatically: false,
          viewer_role: 'buyer',
          method: 'bank_transfer',
          step_expires_at: null,
          escrow_held: true,
        },
      ],
    }),
  ],
  [
    'GET',
    /^\/p2p\/trades\/[^/]+$/,
    (m) => ({
      id: m[0].split('/')[3],
      state: 'awaiting_payment',
      asset: 'USDT',
      amount: '250.000000',
      release_path: 'counterparty_confirmed',
      settles_automatically: false,
      viewer_role: 'buyer',
      method: 'bank_transfer',
      step_expires_at: null,
      escrow_held: true,
    }),
  ],
  [
    'POST',
    /^\/p2p\/trades\/[^/]+\/paid$/,
    (m) => ({ trade_id: m[0].split('/')[3], state: 'payment_marked', released: false }),
  ],
  [
    'POST',
    /^\/p2p\/trades\/[^/]+\/confirm$/,
    (m) => ({ trade_id: m[0].split('/')[3], state: 'released', released: true }),
  ],
  [
    'POST',
    /^\/p2p\/trades\/[^/]+\/dispute$/,
    (m) => ({
      trade_id: m[0].split('/')[3],
      state: 'under_dispute',
      released: false,
      escrow_held: true,
    }),
  ],

  // Instant swap: the second P2P flow, against the platform's own float at the day's published rate.
  //
  // Shaped like the real responses down to the field names, because the last time these fixtures
  // disagreed with the wire format the TS type agreed with the FIXTURES and a broken link shipped
  // (see the note on MovementResult in api.ts). A mock that confirms a bug is worse than no mock.
  ['GET', /^\/transact\/swap\/board$/, () => swapBoard()],
  [
    'POST',
    /^\/transact\/swap\/quote$/,
    (_m, _q, init) => priceSwap(JSON.parse(String(init?.body ?? '{}'))),
  ],
  [
    'POST',
    /^\/transact\/swap$/,
    () => ({
      outcome: 'settled',
      transaction_id: 'txn-swap-mock',
      escrow_id: 'esc-swap-mock',
      correlation_id: 'corr-swap-mock',
    }),
  ],

  // NOTE: a second `POST /p2p/offers` route used to sit here, returning a bare `{ id }`. It was
  // dead -- first match wins and the real one is above -- and it was the more honest of the two,
  // which is the worst way for a duplicate to be arranged. Removed rather than reconciled.
  [
    'POST',
    /^\/p2p\/offers\/[^/]+\/accept$/,
    () => ({ ok: true, trade_id: 't-' + Math.floor(Math.random() * 1e9).toString(36) }),
  ],
  ['GET', /^\/portfolio\/analytics$/, () => ANALYTICS],
  ['GET', /^\/portfolio\/tax$/, () => TAX],
  ['GET', /^\/accounts$/, () => ACCOUNTS],

  // ---------------------------------------------------------------------------------------------
  // TILLS. See the store above for the two rules it enforces.
  // ---------------------------------------------------------------------------------------------
  ['GET', /^\/tills$/, () => ({ tills: readTills().filter((t) => t.participant_id === DEMO_ID) })],
  [
    'GET',
    /^\/tills\/options$/,
    () => ({
      assets: TILL_ASSETS,
      //: One field, so no screen can disclose the simulation on one page and forget it on another.
      custody_is_simulated: TILL_ASSETS.every((a) => a.networks.every((n) => !n.live)),
    }),
  ],
  [
    'POST',
    /^\/tills$/,
    (_m, _q, init) => {
      const body = JSON.parse(String(init?.body ?? '{}')) as { asset?: string; network?: string };
      return issueTill(String(body.asset ?? ''), String(body.network ?? ''));
    },
  ],
  [
    'GET',
    /^\/tills\/[^/]+\/resolve$/,
    (m) => {
      const number = tillKey(decodeURIComponent(m[0].split('/')[2]));
      const mine = readTills().find((t) => t.till_number === number);
      const till = mine ?? OTHER_TILLS.find((t) => t.till_number === number);

      // NOT_FOUND, with the code the client keys on. An inactive till is deliberately the same
      // answer as an unknown one: "that till was retired last month" tells a stranger something
      // about a business they were not given a till to learn.
      if (!till || till.status !== 'active') {
        throw new MockRefusal({
          code: 'NOT_FOUND',
          category: 'validation',
          message: 'We could not find that Till. Check the number and try again.',
          retryable: false,
        });
      }

      return {
        till_number: till.till_number,
        asset: till.asset,
        network: till.network,
        network_tag: till.network_tag,
        //: A display name and a destination. NOT the account behind it.
        recipient_name: mine ? 'Your VASP wallet' : OTHER_TILL_OWNERS[till.till_number],
        recipient_id: till.participant_id,
        wallet_address: till.wallet_address,
        status: till.status,
      };
    },
  ],
  [
    'POST',
    /^\/tills\/[^/]+\/deactivate$/,
    (m) => {
      const number = tillKey(decodeURIComponent(m[0].split('/')[2]));
      const tills = readTills();
      const till = tills.find((t) => t.till_number === number);
      if (!till) {
        throw new MockRefusal({
          code: 'NOT_FOUND',
          category: 'validation',
          message: 'We could not find that Till.',
          retryable: false,
        });
      }
      till.status = 'inactive';
      writeTills(tills);
      return till;
    },
  ],
  ['GET', /^\/lender$/, () => LENDER],
  ['POST', /^\/lender\/applications\/[^/]+\/decision$/, () => ({ ok: true })],
  [
    'POST',
    /^\/lender\/products$/,
    () => ({ id: 'pr-' + Math.floor(Math.random() * 1e9).toString(36) }),
  ],
  ['GET', /^\/merchant$/, () => MERCHANT],
  [
    'POST',
    /^\/merchant\/links$/,
    () => ({ id: 'l-' + Math.floor(Math.random() * 1e9).toString(36) }),
  ],
  ['GET', /^\/business$/, () => BUSINESS],

  // ---------------------------------------------------------------------------
  // THE TOKENIZATION LIFECYCLE (migration 0055).
  //
  // STATEFUL, unlike everything above it, and that is the point: the subject here is a thing that
  // MOVES. A constant fixture would let us build a progress tracker that has never advanced and an
  // Approve button that has never changed anything. `mocks-tokenization.ts` keeps the state and
  // applies the same transition table the database does, so a screen that works here works against
  // svc-tokenization.
  // ---------------------------------------------------------------------------
  ['GET', /^\/tokenize\/lifecycle\/products$/, () => tokenizationFixtures.products()],
  [
    'GET',
    /^\/tokenize\/lifecycle\/products\/([^/]+)$/,
    (m) => refusable(() => tokenizationFixtures.productConfig(m[1])),
  ],
  ['GET', /^\/tokenize\/lifecycle\/upload-policy$/, () => tokenizationFixtures.uploadPolicy()],
  ['GET', /^\/tokenize\/lifecycle\/applications$/, () => tokenizationFixtures.applications()],
  [
    'POST',
    /^\/tokenize\/lifecycle\/applications$/,
    (_m, _q, init) => refusable(() => tokenizationFixtures.start(jsonBody(init))),
  ],
  [
    'GET',
    /^\/tokenize\/lifecycle\/applications\/([^/]+)$/,
    (m) => refusable(() => tokenizationFixtures.application(m[1])),
  ],
  [
    'PATCH',
    /^\/tokenize\/lifecycle\/applications\/([^/]+)$/,
    (m, _q, init) => refusable(() => tokenizationFixtures.update(m[1], jsonBody(init))),
  ],
  [
    'POST',
    /^\/tokenize\/lifecycle\/applications\/([^/]+)\/documents$/,
    (m, _q, init) => {
      // Multipart, exactly as the real upload is. The FILE is read for its name and size only: there
      // is no server here to hash the bytes, and pretending otherwise would put a fabricated
      // fingerprint on a row the UI presents to an owner as evidence.
      const form = init?.body instanceof FormData ? init.body : new FormData();
      const file = form.get('file');
      const name = file instanceof File ? file.name : 'document.pdf';
      const size = file instanceof File ? file.size : 1024;

      return refusable(() =>
        tokenizationFixtures.upload(
          m[1],
          String(form.get('doc_type') ?? 'other_supporting'),
          name,
          size,
          (form.get('replaces') as string) || undefined,
        ),
      );
    },
  ],
  [
    'DELETE',
    /^\/tokenize\/lifecycle\/applications\/([^/]+)\/documents\/([^/]+)$/,
    (m) => refusable(() => tokenizationFixtures.removeDocument(m[1], m[2])),
  ],
  [
    'POST',
    /^\/tokenize\/lifecycle\/applications\/([^/]+)\/submit$/,
    (m) => refusable(() => tokenizationFixtures.submit(m[1])),
  ],
  [
    'POST',
    /^\/tokenize\/lifecycle\/applications\/([^/]+)\/offer\/decision$/,
    (m, _q, init) => {
      const b = jsonBody(init) as { accept?: boolean; reason?: string | null };
      return refusable(() =>
        tokenizationFixtures.decideOffer(m[1], b.accept === true, b.reason ?? null),
      );
    },
  ],
  ['GET', /^\/tokenize\/lifecycle\/portfolio$/, () => tokenizationFixtures.portfolio()],
  [
    'POST',
    /^\/tokenize\/lifecycle\/tokens\/([^/]+)\/listings$/,
    (m, _q, init) => refusable(() => tokenizationFixtures.list(m[1], jsonBody(init))),
  ],
  ['GET', /^\/tokenize\/lifecycle\/listings$/, () => tokenizationFixtures.myListings()],
  [
    'DELETE',
    /^\/tokenize\/lifecycle\/listings\/([^/]+)$/,
    (m) => refusable(() => tokenizationFixtures.cancelListing(m[1])),
  ],
  ['GET', /^\/tokenize\/lifecycle\/market$/, () => tokenizationFixtures.market()],
];

/** Parse a JSON request body. An empty object rather than a throw: a handler may ignore it. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function jsonBody(init?: RequestInit): any {
  if (typeof init?.body !== 'string') return {};
  return JSON.parse(init.body);
}

/**
 * Turn a fixture's refusal into the SAME error a refusing gateway produces.
 *
 * Without this the lifecycle fixtures could only ever succeed, and every refusal path in the UI - the
 * incomplete checklist, the over-listing, the offer that is not yours to decide - would go unexercised
 * until it met a real backend. That is exactly how the withdraw screen came to have no error state.
 */
function refusable<T>(run: () => T): T {
  try {
    return run();
  } catch (e) {
    if (e instanceof FixtureRefusal) {
      throw new MockRefusal({
        code: 'INVALID_REQUEST',
        category: 'policy',
        message: e.message,
        retryable: false,
      });
    }
    throw e;
  }
}

// ===========================================================================
// THE TWO-SIDED CREDIT MARKETPLACE (ARCH 05).
//
// The only fixture block in this file with REAL STATE, and it has to be: the flow the credit program
// describes is a conversation between three parties over time -- the borrower pledges, the Trust
// holds, financers offer, the borrower accepts, the financer approves, money moves, the collateral
// comes back -- and a set of static fixtures can render every screen while proving none of it joins
// up. A pledge that does not reduce availability is a screenshot, not a walkthrough.
//
// So this store enforces, in the browser, the same handful of rules the database enforces for real:
//
//   * available = owned - everything encumbering               (credit.available_bps)
//   * you cannot pledge more than is available                 (claim_collateral, guard 8)
//   * the amount cannot exceed collateral x haircut x LTV      (assert_request_within_ltv)
//   * accepting one offer declines every other, atomically     (one_accepted_offer_per_request)
//   * an offer is NOT a loan; approval is what creates one     (section 38)
//
// It is a STAGE SET and not a control: flip VITE_UI_MOCKS off and every one of those is enforced by
// the server instead. What it buys is that a screen which works here is a screen that asked the right
// questions, rather than one that never asked.
// ===========================================================================

const CREDIT_STORE_KEY = 'lipa.ui-mocks.credit.v1';

/** The demo participant also plays the financer, so both doors are walkable from one session. */
const DEMO_FINANCER_ID = 'demo0003-0000-4000-8000-000000000003';

interface MockHolding {
  token_id: string;
  asset_id: string;
  asset_name: string;
  asset_class: string;
  owned_bps: number;
  valuation_minor: string;
  valuation_asset: string;
  /** Present only where the holding is deliberately staged as ineligible. */
  blocked_reason?: string;
  valuation_is_stale?: boolean;
}

interface MockPosition {
  id: string;
  request_id: string;
  loan_id: string | null;
  token_id: string;
  asset_id: string;
  asset_name: string;
  asset_class: string;
  holder_id: string;
  pledged_bps: number;
  valuation_minor: string;
  valuation_asset: string;
  haircut_bps: number;
  eligible_minor: string;
  lien_priority: number;
  state: string;
  pledged_at: string;
  verified_at: string | null;
  released_at: string | null;
}

interface MockCreditState {
  requests: Record<string, Record<string, unknown>>;
  positions: MockPosition[];
  offers: Record<string, unknown>[];
  loans: Record<string, unknown>[];
  marginCalls: Record<string, unknown>[];
  //: Valuation overrides, keyed by token. The fixture holdings are a const; a margin call cannot be
  //: walked through without the market moving, and on the real platform that movement arrives as a
  //: valuer confirming a new `tokenization.valuation`. This is the stage set's stand-in for one.
  valuations: Record<string, string>;
  transitions: Record<
    string,
    {
      from_state: string | null;
      to_state: string;
      actor_kind: string;
      actor_role: string | null;
      reason: string | null;
      occurred_at: string;
    }[]
  >;
  seq: number;
}

/** The products. Every commercial term a screen shows comes from HERE, mirroring the product table. */
const CREDIT_PRODUCTS = [
  {
    id: 'prd-commercial-mortgage',
    name: 'Commercial Mortgage',
    description:
      'Long-dated finance secured on verified commercial property held by a special purpose trust.',
    lender_id: DEMO_FINANCER_ID,
    lender_name: 'Rafiki Credit Ltd',
    asset_classes: ['real_estate', 'land'],
    max_ltv_bps: 6000,
    margin_call_ltv_bps: 6500,
    liquidation_ltv_bps: 7000,
    haircut_bps: 2000,
    cure_period_hours: 48,
    rate_bps: 1650,
    tenor_days: 730,
    tenor_options_months: [12, 24, 36, 60],
    repayment_frequency: 'monthly',
    grace_period_days: 7,
    min_amount_minor: '50000000',
    max_amount_minor: '500000000000',
    amount_asset: 'KES',
    fee_schedule: [
      {
        code: 'arrangement',
        label: 'Arrangement fee',
        basis: 'bps_of_principal',
        bps: 150,
        when: 'on_disbursement',
      },
      {
        code: 'valuation',
        label: 'Valuation fee',
        basis: 'fixed',
        amount_minor: '3500000',
        when: 'on_disbursement',
      },
    ],
    required_documents: ['title_deed', 'valuation_report', 'insurance_certificate'],
    proceeds_applicable: true,
    eligibility_notes:
      'The property must be held by a trust or SPV and carry a valuation under 180 days old.',
  },
  {
    id: 'prd-bridge',
    name: 'Bridge Financing',
    description: 'Short-dated cover between a committed sale and its completion.',
    lender_id: DEMO_FINANCER_ID,
    lender_name: 'Mavuno Finance Ltd',
    asset_classes: ['real_estate', 'land'],
    max_ltv_bps: 5000,
    margin_call_ltv_bps: 5500,
    liquidation_ltv_bps: 6000,
    haircut_bps: 2500,
    cure_period_hours: 48,
    rate_bps: 2400,
    tenor_days: 365,
    tenor_options_months: [3, 6, 12],
    repayment_frequency: 'bullet',
    grace_period_days: 5,
    min_amount_minor: '20000000',
    max_amount_minor: '200000000000',
    amount_asset: 'KES',
    fee_schedule: [
      {
        code: 'arrangement',
        label: 'Arrangement fee',
        basis: 'bps_of_principal',
        bps: 250,
        when: 'on_disbursement',
      },
    ],
    required_documents: ['title_deed', 'sale_agreement'],
    proceeds_applicable: false,
    eligibility_notes: 'Requires evidence of the committed exit.',
  },
  {
    id: 'prd-rental-advance',
    name: 'Rental Advance',
    description: 'An advance against contracted rental income from a tokenized property.',
    lender_id: DEMO_FINANCER_ID,
    lender_name: 'Rafiki Credit Ltd',
    asset_classes: ['real_estate'],
    max_ltv_bps: 4000,
    margin_call_ltv_bps: 4500,
    liquidation_ltv_bps: 5000,
    haircut_bps: 3000,
    cure_period_hours: 72,
    rate_bps: 1900,
    tenor_days: 365,
    tenor_options_months: [6, 12, 24],
    repayment_frequency: 'monthly',
    grace_period_days: 10,
    min_amount_minor: '10000000',
    max_amount_minor: '50000000000',
    amount_asset: 'KES',
    fee_schedule: [
      {
        code: 'arrangement',
        label: 'Arrangement fee',
        basis: 'bps_of_principal',
        bps: 100,
        when: 'on_disbursement',
      },
    ],
    required_documents: ['tenancy_schedule', 'valuation_report'],
    proceeds_applicable: true,
    eligibility_notes: 'Rental proceeds are directed to the trust for the life of the advance.',
  },
  {
    id: 'prd-working-capital',
    name: 'Asset-backed Working Capital',
    description: 'A revolving facility secured on tokenized inventory or receivables.',
    lender_id: DEMO_FINANCER_ID,
    lender_name: 'Mavuno Finance Ltd',
    asset_classes: ['inventory', 'receivable'],
    max_ltv_bps: 5000,
    margin_call_ltv_bps: 5500,
    liquidation_ltv_bps: 6000,
    haircut_bps: 3500,
    cure_period_hours: 48,
    rate_bps: 2100,
    tenor_days: 365,
    tenor_options_months: [3, 6, 12],
    repayment_frequency: 'quarterly',
    grace_period_days: 5,
    min_amount_minor: '10000000',
    max_amount_minor: '100000000000',
    amount_asset: 'KES',
    fee_schedule: [
      {
        code: 'arrangement',
        label: 'Arrangement fee',
        basis: 'bps_of_principal',
        bps: 200,
        when: 'on_disbursement',
      },
    ],
    required_documents: ['stock_report', 'audited_accounts'],
    proceeds_applicable: false,
    eligibility_notes: 'Requires a current stock or receivables report.',
  },
  {
    id: 'prd-vehicle-equity',
    name: 'Vehicle Equity Loan',
    description: 'Finance released against equity in a tokenized commercial vehicle.',
    lender_id: DEMO_FINANCER_ID,
    lender_name: 'Rafiki Credit Ltd',
    asset_classes: ['vehicle'],
    max_ltv_bps: 4500,
    margin_call_ltv_bps: 5000,
    liquidation_ltv_bps: 5500,
    haircut_bps: 4000,
    cure_period_hours: 48,
    rate_bps: 2600,
    tenor_days: 730,
    tenor_options_months: [12, 24, 36],
    repayment_frequency: 'monthly',
    grace_period_days: 7,
    min_amount_minor: '5000000',
    max_amount_minor: '20000000000',
    amount_asset: 'KES',
    fee_schedule: [
      {
        code: 'arrangement',
        label: 'Arrangement fee',
        basis: 'bps_of_principal',
        bps: 300,
        when: 'on_disbursement',
      },
    ],
    required_documents: ['logbook', 'valuation_report', 'insurance_certificate'],
    proceeds_applicable: false,
    eligibility_notes: 'Comprehensive insurance must be assigned for the life of the loan.',
  },
  {
    id: 'prd-crop-finance',
    name: 'Seasonal Crop Finance',
    description: 'Pre-harvest finance against a tokenized warehouse receipt.',
    lender_id: DEMO_FINANCER_ID,
    lender_name: 'Mavuno Finance Ltd',
    asset_classes: ['inventory'],
    max_ltv_bps: 4000,
    margin_call_ltv_bps: 4500,
    liquidation_ltv_bps: 5000,
    haircut_bps: 4000,
    cure_period_hours: 72,
    rate_bps: 1750,
    tenor_days: 270,
    tenor_options_months: [6, 9],
    repayment_frequency: 'seasonal',
    grace_period_days: 14,
    min_amount_minor: '5000000',
    max_amount_minor: '30000000000',
    amount_asset: 'KES',
    fee_schedule: [
      {
        code: 'arrangement',
        label: 'Arrangement fee',
        basis: 'bps_of_principal',
        bps: 175,
        when: 'on_disbursement',
      },
    ],
    required_documents: ['warehouse_receipt', 'offtake_agreement'],
    proceeds_applicable: true,
    eligibility_notes: 'Requires a warehouse receipt from a licensed operator.',
  },
];

/**
 * The borrower's tokenized holdings.
 *
 * Three of them, and the third is deliberately INELIGIBLE. A fixture set in which everything works is
 * one where the "why can I not use this" path never renders, and that path is most of what a borrower
 * needs from this screen.
 */
const CREDIT_HOLDINGS: MockHolding[] = [
  {
    token_id: 'tok-abc-apartments',
    asset_id: 'ast-abc-apartments',
    asset_name: 'ABC Apartments, Kilimani',
    asset_class: 'real_estate',
    owned_bps: 10000,
    valuation_minor: '2000000000',
    valuation_asset: 'KES',
  },
  {
    token_id: 'tok-ngong-parcel',
    asset_id: 'ast-ngong-parcel',
    asset_name: 'Ngong Road parcel LR 209/4471',
    asset_class: 'land',
    owned_bps: 4500,
    valuation_minor: '850000000',
    valuation_asset: 'KES',
  },
  {
    token_id: 'tok-thika-warehouse',
    asset_id: 'ast-thika-warehouse',
    asset_name: 'Thika warehouse, Phase 2',
    asset_class: 'real_estate',
    owned_bps: 10000,
    valuation_minor: '1200000000',
    valuation_asset: 'KES',
    valuation_is_stale: true,
    blocked_reason: "This asset's valuation is out of date and must be refreshed.",
  },
];

let creditCache: MockCreditState | null = null;

function emptyCreditState(): MockCreditState {
  return {
    requests: {},
    positions: [],
    offers: [],
    loans: [],
    marginCalls: [],
    valuations: {},
    transitions: {},
    seq: 1000,
  };
}

function readCredit(): MockCreditState {
  if (creditCache) return creditCache;
  try {
    const raw = globalThis.localStorage?.getItem(CREDIT_STORE_KEY);
    // The stored object is SPREAD OVER a fresh empty one rather than used directly. `marginCalls`
    // post-dates the first version of this store, so a walkthrough saved before it existed arrives
    // without the key and every read of it throws. Losing a developer's in-progress walkthrough on a
    // schema change is the worse trade.
    creditCache = raw
      ? { ...emptyCreditState(), ...(JSON.parse(raw) as Partial<MockCreditState>) }
      : emptyCreditState();
  } catch {
    creditCache = emptyCreditState();
  }
  return creditCache;
}

function writeCredit(state: MockCreditState): void {
  creditCache = state;
  try {
    globalThis.localStorage?.setItem(CREDIT_STORE_KEY, JSON.stringify(state));
  } catch {
    // A private-mode browser is not an error worth surfacing in a stage set.
  }
}

const ENCUMBERING = ['pledged', 'encumbered', 'under_enforcement', 'realization_pending'];

/** The current valuation of a holding, honouring any revaluation the walkthrough has applied. */
function valuationOf(state: MockCreditState, tokenId: string): string {
  const holding = CREDIT_HOLDINGS.find((h) => h.token_id === tokenId);
  return state.valuations[tokenId] ?? holding?.valuation_minor ?? '0';
}

/** The ONE definition of available units, mirroring `credit.available_bps`. */
function availableBps(state: MockCreditState, tokenId: string): number {
  const holding = CREDIT_HOLDINGS.find((h) => h.token_id === tokenId);
  if (!holding) return 0;
  const used = state.positions
    .filter((p) => p.token_id === tokenId && ENCUMBERING.includes(p.state))
    .reduce((sum, p) => sum + p.pledged_bps, 0);
  return Math.max(0, holding.owned_bps - used);
}

function eligibleMinor(position: {
  valuation_minor: string;
  pledged_bps: number;
  haircut_bps: number;
}): string {
  const value = BigInt(position.valuation_minor);
  const afterShare = (value * BigInt(position.pledged_bps)) / 10000n;
  return String((afterShare * BigInt(10000 - position.haircut_bps)) / 10000n);
}

function requestCollateral(state: MockCreditState, requestId: string): MockPosition[] {
  return state.positions.filter((p) => p.request_id === requestId && p.state !== 'released');
}

function eligibleCollateralFor(state: MockCreditState, requestId: string): bigint {
  return state.positions
    .filter(
      (p) => p.request_id === requestId && (p.state === 'pledged' || p.state === 'encumbered'),
    )
    .reduce((sum, p) => sum + BigInt(p.eligible_minor), 0n);
}

function maxFinancing(state: MockCreditState, requestId: string): bigint {
  const request = state.requests[requestId];
  const product = CREDIT_PRODUCTS.find((p) => p.id === request?.['product_id']);
  if (!product) return 0n;
  return (eligibleCollateralFor(state, requestId) * BigInt(product.max_ltv_bps)) / 10000n;
}

function transition(
  state: MockCreditState,
  requestId: string,
  to: string,
  actorKind = 'participant',
  actorRole: string | null = null,
  reason: string | null = null,
): void {
  const request = state.requests[requestId];
  const from = (request?.['state'] as string | undefined) ?? null;
  state.transitions[requestId] = [
    ...(state.transitions[requestId] ?? []),
    {
      from_state: from,
      to_state: to,
      actor_kind: actorKind,
      actor_role: actorRole,
      reason,
      occurred_at: iso(),
    },
  ];
  if (request) request['state'] = to;
}

function requestView(state: MockCreditState, requestId: string): Record<string, unknown> {
  const request = state.requests[requestId];
  const product = CREDIT_PRODUCTS.find((p) => p.id === request['product_id']);
  return {
    ...request,
    product_name: product?.name ?? null,
    eligible_collateral_minor: String(eligibleCollateralFor(state, requestId)),
    max_financing_minor: String(maxFinancing(state, requestId)),
    live_offers: state.offers.filter(
      (o) =>
        o['request_id'] === requestId &&
        ['submitted', 'under_review'].includes(o['state'] as string),
    ).length,
  };
}

function requestDetail(state: MockCreditState, requestId: string): Record<string, unknown> {
  return {
    ...requestView(state, requestId),
    collateral: requestCollateral(state, requestId),
    offers: state.offers.filter((o) => o['request_id'] === requestId),
    timeline: state.transitions[requestId] ?? [],
  };
}

/**
 * The key facts (FR-U-CRD-014). Computed the same way the engine computes them: simple interest over
 * the tenor, plus the product's declared fees, in integer minor units throughout.
 */
function keyFacts(
  productId: string,
  principalMinor: string,
  rateBps: number,
  tenorMonths: number,
  frequency: string,
): Record<string, unknown> {
  const product = CREDIT_PRODUCTS.find((p) => p.id === productId);
  const principal = BigInt(principalMinor);
  const interest = (principal * BigInt(rateBps) * BigInt(tenorMonths)) / (10000n * 12n);

  let fees = 0n;
  const items = (product?.fee_schedule ?? []).map((fee) => {
    const value =
      fee.basis === 'bps_of_principal'
        ? (principal * BigInt(fee.bps ?? 0)) / 10000n
        : BigInt(fee.amount_minor ?? '0');
    fees += value;
    return { code: fee.code, label: fee.label, amount_minor: String(value), when: fee.when };
  });

  const total = principal + interest + fees;
  return {
    principal_minor: String(principal),
    interest_minor: String(interest),
    fees_minor: String(fees),
    total_repayable_minor: String(total),
    cost_of_credit_minor: String(interest + fees),
    asset: 'KES',
    rate_bps: rateBps,
    tenor_months: tenorMonths,
    repayment_frequency: frequency,
    fee_items: items,
  };
}

/** Two rival financers, so the offer comparison screen has something to compare. */
const MOCK_FINANCERS = [
  {
    id: DEMO_FINANCER_ID,
    name: 'Rafiki Credit Ltd',
    productId: 'prd-commercial-mortgage',
    rate: 1650,
  },
  {
    id: 'demo0004-0000-4000-8000-000000000004',
    name: 'Mavuno Finance Ltd',
    productId: 'prd-bridge',
    rate: 1820,
  },
];

function creditRoutes(): [string, RegExp, Handler][] {
  return [
    [
      'GET',
      /^\/credit\/products$/,
      (_m, query) => {
        const assetClass = query.get('asset_class');
        return {
          products: assetClass
            ? CREDIT_PRODUCTS.filter((p) => p.asset_classes.includes(assetClass))
            : CREDIT_PRODUCTS,
        };
      },
    ],

    [
      'GET',
      /^\/credit\/collateral\/eligible$/,
      (_m, query) => {
        const state = readCredit();
        const productId = query.get('product_id');
        const product = CREDIT_PRODUCTS.find((p) => p.id === productId);
        const collateral = CREDIT_HOLDINGS.map((h) => {
          const available = availableBps(state, h.token_id);
          const reasons: string[] = [];
          if (h.blocked_reason) reasons.push(h.blocked_reason);
          if (available <= 0) reasons.push('All of your units in this asset are already pledged.');
          if (product && !product.asset_classes.includes(h.asset_class))
            reasons.push('This credit product does not accept this class of asset.');
          return {
            token_id: h.token_id,
            asset_id: h.asset_id,
            asset_name: h.asset_name,
            asset_class: h.asset_class,
            owned_bps: h.owned_bps,
            available_bps: available,
            pledged_bps: h.owned_bps - available,
            valuation_minor: h.valuation_minor,
            valuation_asset: h.valuation_asset,
            valuation_is_stale: Boolean(h.valuation_is_stale),
            eligible: reasons.length === 0,
            reasons,
          };
        });
        return {
          collateral,
          has_any: collateral.some((c) => c.eligible),
          holds_tokens: collateral.length > 0,
        };
      },
    ],

    [
      'GET',
      /^\/credit\/collateral$/,
      () => {
        const state = readCredit();
        return {
          collateral: state.positions.map((p) => ({
            ...p,
            loan_state:
              (state.loans.find((l) => l['id'] === p.loan_id)?.['state'] as string) ?? null,
            financer_name:
              (state.offers.find(
                (o) => o['id'] === state.loans.find((l) => l['id'] === p.loan_id)?.['offer_id'],
              )?.['financer_name'] as string) ?? null,
            request_reference: (state.requests[p.request_id]?.['reference'] as string) ?? null,
          })),
        };
      },
    ],

    [
      'GET',
      /^\/credit\/requests$/,
      () => {
        const state = readCredit();
        return {
          requests: Object.keys(state.requests)
            .map((id) => requestView(state, id))
            .sort((a, b) => String(b['created_at']).localeCompare(String(a['created_at']))),
        };
      },
    ],

    [
      'POST',
      /^\/credit\/requests$/,
      (_m, _q, init) => {
        const state = readCredit();
        const body = JSON.parse(String(init?.body ?? '{}'));
        const product = CREDIT_PRODUCTS.find((p) => p.id === body.product_id);
        if (!product) {
          throw new MockRefusal({
            code: 'INVALID_REQUEST',
            category: 'validation',
            message: 'No such credit product.',
            retryable: false,
          });
        }
        const amountMinor = String(toBaseUnits(String(body.amount)));
        if (product.min_amount_minor && BigInt(amountMinor) < BigInt(product.min_amount_minor)) {
          throw new MockRefusal({
            code: 'INVALID_REQUEST',
            category: 'policy',
            message: 'That amount is below what this credit product lends.',
            retryable: false,
          });
        }
        const id = `req-${state.seq}`;
        state.requests[id] = {
          id,
          reference: `CR-${state.seq}`,
          borrower_id: DEMO_ID,
          product_id: product.id,
          amount_minor: amountMinor,
          asset: body.asset ?? 'KES',
          tenor_months: body.tenor_months,
          repayment_frequency: body.repayment_frequency ?? product.repayment_frequency,
          purpose: body.purpose,
          state: 'draft',
          financing_deadline: null,
          selected_offer_id: null,
          rejected_reason: null,
          created_at: iso(),
          submitted_at: null,
          accepted_at: null,
        };
        state.seq += 1;
        transition(state, id, 'draft');
        // `transition` reads the previous state, which for a brand-new request is 'draft' already, so
        // the first row would read draft -> draft. Correct it to the shape the backend writes.
        const rows = state.transitions[id];
        if (rows?.[0]) rows[0].from_state = null;
        writeCredit(state);
        return requestView(state, id);
      },
    ],

    [
      'GET',
      /^\/credit\/requests\/[^/]+$/,
      (m) => {
        const state = readCredit();
        const id = m[0].split('/').pop() as string;
        if (!state.requests[id]) {
          throw new MockRefusal({
            code: 'INVALID_REQUEST',
            category: 'validation',
            message: 'No such borrowing request.',
            retryable: false,
          });
        }
        return requestDetail(state, id);
      },
    ],

    [
      'POST',
      /^\/credit\/requests\/[^/]+\/collateral$/,
      (m, _q, init) => {
        const state = readCredit();
        const id = m[0].split('/')[3] as string;
        const body = JSON.parse(String(init?.body ?? '{}'));
        const request = state.requests[id];
        const product = CREDIT_PRODUCTS.find((p) => p.id === request?.['product_id']);
        const holding = CREDIT_HOLDINGS.find((h) => h.token_id === body.token_id);

        if (!request || !product || !holding) {
          throw new MockRefusal({
            code: 'INVALID_REQUEST',
            category: 'validation',
            message: 'We could not find that request or that asset.',
            retryable: false,
          });
        }
        if (request['state'] !== 'draft') {
          throw new MockRefusal({
            code: 'APPROVAL_REQUIRED',
            category: 'policy',
            message:
              'This borrowing request has moved on and its collateral can no longer be changed.',
            retryable: false,
          });
        }

        // GUARD 8, in miniature. The server serializes this on the ownership row; here it is the same
        // arithmetic, so a screen that can over-pledge here would over-pledge there.
        const available = availableBps(state, body.token_id);
        if (body.bps > available) {
          throw new MockRefusal({
            code: 'TOKEN_ALREADY_PLEDGED',
            category: 'policy',
            message:
              'Some of those units are already pledged as collateral and cannot be pledged again.',
            retryable: false,
          });
        }

        const position: MockPosition = {
          id: `pos-${state.seq}`,
          request_id: id,
          loan_id: null,
          token_id: holding.token_id,
          asset_id: holding.asset_id,
          asset_name: holding.asset_name,
          asset_class: holding.asset_class,
          holder_id: DEMO_ID,
          pledged_bps: body.bps,
          valuation_minor: valuationOf(state, holding.token_id),
          valuation_asset: holding.valuation_asset,
          haircut_bps: product.haircut_bps,
          eligible_minor: '0',
          lien_priority: body.lien_priority ?? 1,
          state: 'pledged',
          pledged_at: iso(),
          verified_at: null,
          released_at: null,
        };
        position.eligible_minor = eligibleMinor(position);
        state.seq += 1;
        state.positions.push(position);
        writeCredit(state);
        return position;
      },
    ],

    [
      'DELETE',
      /^\/credit\/requests\/[^/]+\/collateral\/[^/]+$/,
      (m) => {
        const state = readCredit();
        const positionId = m[0].split('/').pop() as string;
        const position = state.positions.find((p) => p.id === positionId);
        if (position) {
          position.state = 'released';
          position.released_at = iso();
        }
        writeCredit(state);
        return { ok: true };
      },
    ],

    [
      'POST',
      /^\/credit\/requests\/[^/]+\/submit$/,
      (m) => {
        const state = readCredit();
        const id = m[0].split('/')[3] as string;
        const request = state.requests[id];
        if (!request) {
          throw new MockRefusal({
            code: 'INVALID_REQUEST',
            category: 'validation',
            message: 'No such borrowing request.',
            retryable: false,
          });
        }
        if (requestCollateral(state, id).length === 0) {
          throw new MockRefusal({
            code: 'TOKEN_ALREADY_PLEDGED',
            category: 'policy',
            message:
              'You do not have an eligible tokenized asset pledged. Tokenize an asset first, then come back and borrow against it.',
            retryable: false,
          });
        }

        // The LTV ceiling. Refused here for the same reason the database refuses it: the amount a
        // borrower asks for is a statement to somebody else the moment it leaves draft.
        const max = maxFinancing(state, id);
        if (BigInt(String(request['amount_minor'])) > max) {
          throw new MockRefusal({
            code: 'CAP_EXCEEDED',
            category: 'policy',
            message:
              "That is more than this collateral supports at this product's maximum loan to value.",
            retryable: false,
          });
        }

        request['submitted_at'] = iso();
        transition(state, id, 'submitted');
        // The sandbox walks the platform, collateral and Trust reviews immediately, so the demo is
        // walkable end to end. A real deployment has people in these three queues.
        transition(state, id, 'under_review', 'operator', 'compliance_officer');
        transition(state, id, 'collateral_verification', 'operator', 'trustee');
        transition(state, id, 'trust_review', 'operator', 'trustee');
        for (const position of requestCollateral(state, id)) {
          position.state = 'encumbered';
          position.verified_at = iso();
        }
        transition(state, id, 'open_for_financing', 'operator', 'trustee');
        request['financing_deadline'] = iso(14 * DAY);

        // Two financers look at it and both bid. This is a stage set: on the real platform they arrive
        // when they arrive, and a request can sit open with no offers at all.
        for (const [n, financer] of MOCK_FINANCERS.entries()) {
          const product = CREDIT_PRODUCTS.find((p) => p.id === financer.productId);
          const amount = String(request['amount_minor']);
          const tenor = Number(request['tenor_months']);
          const facts = keyFacts(
            financer.productId,
            amount,
            financer.rate,
            tenor,
            String(request['repayment_frequency']),
          );
          state.offers.push({
            id: `off-${state.seq + n}`,
            request_id: id,
            financer_id: financer.id,
            financer_name: financer.name,
            product_id: financer.productId,
            product_name: product?.name ?? null,
            amount_minor: amount,
            asset: 'KES',
            rate_bps: financer.rate,
            tenor_months: tenor,
            repayment_frequency: String(request['repayment_frequency']),
            fees_minor: String(facts['fees_minor']),
            total_repayable_minor: String(facts['total_repayable_minor']),
            key_facts: facts,
            conditions:
              n === 0
                ? [
                    'Fire and perils insurance to be assigned to the Trust.',
                    'Rental proceeds directed to the Trust.',
                  ]
                : ['Personal guarantee from the principal shareholder.'],
            state: 'submitted',
            decline_reason: null,
            approved_at: null,
            created_at: iso(),
            expires_at: iso(7 * DAY),
          });
        }
        state.seq += MOCK_FINANCERS.length;
        transition(state, id, 'financing_interest_received', 'system');
        writeCredit(state);
        return requestDetail(state, id);
      },
    ],

    [
      'POST',
      /^\/credit\/requests\/[^/]+\/cancel$/,
      (m) => {
        const state = readCredit();
        const id = m[0].split('/')[3] as string;
        transition(state, id, 'cancelled', 'participant', null, 'Cancelled by the borrower.');
        for (const position of requestCollateral(state, id)) {
          position.state = 'released';
          position.released_at = iso();
        }
        for (const offer of state.offers.filter((o) => o['request_id'] === id)) {
          if (['submitted', 'under_review'].includes(offer['state'] as string))
            offer['state'] = 'cancelled';
        }
        writeCredit(state);
        return requestView(state, id);
      },
    ],

    [
      'POST',
      /^\/credit\/requests\/[^/]+\/accept$/,
      (m, _q, init) => {
        const state = readCredit();
        const id = m[0].split('/')[3] as string;
        const body = JSON.parse(String(init?.body ?? '{}'));
        const chosen = state.offers.find((o) => o['id'] === body.offer_id);
        if (!chosen) {
          throw new MockRefusal({
            code: 'INVALID_REQUEST',
            category: 'validation',
            message: 'No such financing offer.',
            retryable: false,
          });
        }

        // One transaction, both effects. The window between "accepted A" and "declined B" is a window
        // in which two financers both believe they are funding this collateral.
        for (const offer of state.offers.filter((o) => o['request_id'] === id)) {
          if (offer['id'] === body.offer_id) offer['state'] = 'accepted';
          else if (['submitted', 'under_review'].includes(offer['state'] as string)) {
            offer['state'] = 'rejected';
            offer['decline_reason'] = 'The borrower accepted another offer.';
          }
        }
        state.requests[id]['selected_offer_id'] = body.offer_id;
        state.requests[id]['accepted_at'] = iso();
        transition(state, id, 'financing_selected');
        transition(state, id, 'borrower_acceptance_pending');
        transition(
          state,
          id,
          'dcp_approval_pending',
          'participant',
          null,
          'Borrower accepted the financing offer.',
        );
        writeCredit(state);
        return requestDetail(state, id);
      },
    ],

    ['GET', /^\/credit\/loans$/, () => ({ loans: readCredit().loans })],

    [
      'GET',
      /^\/credit\/loans\/[^/]+$/,
      (m) => {
        const state = readCredit();
        const id = m[0].split('/').pop() as string;
        const loan = state.loans.find((l) => l['id'] === id);
        if (!loan) {
          throw new MockRefusal({
            code: 'INVALID_REQUEST',
            category: 'validation',
            message: 'No such loan.',
            retryable: false,
          });
        }
        return {
          ...loan,
          collateral: state.positions.filter((p) => p.loan_id === id),
        };
      },
    ],

    [
      'POST',
      /^\/credit\/loans\/[^/]+\/disburse$/,
      (m) => {
        const state = readCredit();
        const id = m[0].split('/')[3] as string;
        const loan = state.loans.find((l) => l['id'] === id);
        if (!loan || loan['state'] !== 'approved') {
          throw new MockRefusal({
            code: 'INVALID_REQUEST',
            category: 'validation',
            message: 'This loan is not awaiting disbursement.',
            retryable: false,
          });
        }
        loan['state'] = 'disbursed';
        loan['disbursed_at'] = iso();
        loan['next_due_at'] = iso(30 * DAY);
        writeCredit(state);
        return { movement: { outcome: 'settled' }, loan };
      },
    ],

    [
      'POST',
      /^\/credit\/loans\/[^/]+\/repay$/,
      (m, _q, init) => {
        const state = readCredit();
        const id = m[0].split('/')[3] as string;
        const body = JSON.parse(String(init?.body ?? '{}'));
        const loan = state.loans.find((l) => l['id'] === id);
        if (!loan) {
          throw new MockRefusal({
            code: 'INVALID_REQUEST',
            category: 'validation',
            message: 'No such loan.',
            retryable: false,
          });
        }

        const amount = BigInt(String(toBaseUnits(String(body.amount))));
        const principal = BigInt(String(loan['principal_minor']));
        const total = BigInt(String(loan['total_repayable_minor']));
        const feesDue =
          BigInt(String(loan['fees_minor'])) - BigInt(String(loan['fees_paid_minor']));
        const interestDue =
          total -
          principal -
          BigInt(String(loan['fees_minor'])) -
          BigInt(String(loan['interest_paid_minor']));
        const principalDue = principal - BigInt(String(loan['principal_repaid_minor']));

        if (amount > feesDue + interestDue + principalDue) {
          throw new MockRefusal({
            code: 'INVALID_REQUEST',
            category: 'validation',
            message: 'That is more than this loan needs to settle.',
            retryable: false,
          });
        }

        // Fees, then interest, then principal. The same waterfall the engine applies.
        let left = amount;
        const toFees = left < feesDue ? left : feesDue;
        left -= toFees;
        const toInterest = left < interestDue ? left : interestDue;
        left -= toInterest;
        const toPrincipal = left < principalDue ? left : principalDue;

        loan['fees_paid_minor'] = String(BigInt(String(loan['fees_paid_minor'])) + toFees);
        loan['interest_paid_minor'] = String(
          BigInt(String(loan['interest_paid_minor'])) + toInterest,
        );
        const repaid = BigInt(String(loan['principal_repaid_minor'])) + toPrincipal;
        loan['principal_repaid_minor'] = String(repaid);
        loan['outstanding_minor'] = String(principal - repaid);
        loan['repaid_pct'] = Number((repaid * 1000n) / principal) / 10;

        const schedule = loan['schedule'] as {
          total_minor: string;
          paid_minor: string;
          state: string;
        }[];
        let remaining = amount;
        for (const row of schedule) {
          if (remaining <= 0n) break;
          const owed = BigInt(row.total_minor) - BigInt(row.paid_minor);
          const applied = remaining < owed ? remaining : owed;
          remaining -= applied;
          row.paid_minor = String(BigInt(row.paid_minor) + applied);
          row.state = BigInt(row.paid_minor) >= BigInt(row.total_minor) ? 'paid' : 'partial';
        }

        (loan['repayments'] as unknown[]).push({
          id: `rep-${state.seq}`,
          transaction_id: `txn-${state.seq}`,
          amount_minor: String(amount),
          asset: 'KES',
          applied_fees_minor: String(toFees),
          applied_interest_minor: String(toInterest),
          applied_principal_minor: String(toPrincipal),
          interest_rebate_minor: '0',
          is_early_settlement: Boolean(body.early_settlement),
          paid_at: iso(),
        });
        state.seq += 1;

        if (repaid >= principal) {
          loan['state'] = 'closed';
          loan['closed_at'] = iso();
          // Section 24: the collateral comes back the moment the debt is discharged.
          for (const position of state.positions.filter((p) => p.loan_id === id)) {
            position.state = 'released';
            position.released_at = iso();
          }
        } else {
          loan['state'] = 'repaying';
        }

        // A partial repayment is the other remedy a margin call notice offers, so paying down has to
        // be able to close one. Recomputed rather than assumed: the call closes only if the loan is
        // genuinely back under the ceiling.
        cureIfCovered(state, id);

        writeCredit(state);
        return { movement: { outcome: 'settled' }, loan };
      },
    ],

    // -- COVERAGE AND THE MARGIN CALL --------------------------------------

    [
      'GET',
      /^\/credit\/loans\/[^/]+\/coverage$/,
      (m) => {
        const state = readCredit();
        const id = m[0].split('/')[3] as string;
        return {
          coverage: loanCoverage(state, id),
          margin_calls: state.marginCalls.filter((c) => c['loan_id'] === id),
        };
      },
    ],

    [
      'POST',
      /^\/credit\/loans\/[^/]+\/collateral$/,
      (m, _q, init) => {
        const state = readCredit();
        const id = m[0].split('/')[3] as string;
        const body = JSON.parse(String(init?.body ?? '{}'));
        const loan = state.loans.find((l) => l['id'] === id);
        const holding = CREDIT_HOLDINGS.find((h) => h.token_id === body.token_id);
        if (!loan || !holding) {
          throw new MockRefusal({
            code: 'INVALID_REQUEST',
            category: 'validation',
            message: 'We could not find that loan or that asset.',
            retryable: false,
          });
        }
        // Guard 8 applies to a top-up exactly as it does to an original pledge: a borrower cannot
        // cure one loan's margin call with units that are already securing another.
        if (body.bps > availableBps(state, body.token_id)) {
          throw new MockRefusal({
            code: 'TOKEN_ALREADY_PLEDGED',
            category: 'policy',
            message: 'Some of those units are already pledged as collateral.',
            retryable: false,
          });
        }
        const product = CREDIT_PRODUCTS.find((p) => p.id === loan['product_id']);
        const position: MockPosition = {
          id: `pos-${state.seq}`,
          request_id: String(loan['request_id']),
          loan_id: id,
          token_id: holding.token_id,
          asset_id: holding.asset_id,
          asset_name: holding.asset_name,
          asset_class: holding.asset_class,
          holder_id: DEMO_ID,
          pledged_bps: body.bps,
          valuation_minor: valuationOf(state, holding.token_id),
          valuation_asset: holding.valuation_asset,
          haircut_bps: product?.haircut_bps ?? 2000,
          eligible_minor: '0',
          // A top-up ranks BEHIND the original security. Priority 1 would silently re-order the
          // liens a lender already holds.
          lien_priority: 2,
          state: 'encumbered',
          pledged_at: iso(),
          verified_at: iso(),
          released_at: null,
        };
        position.eligible_minor = eligibleMinor(position);
        state.seq += 1;
        state.positions.push(position);
        cureIfCovered(state, id);
        writeCredit(state);
        return loanCoverage(state, id);
      },
    ],

    [
      'POST',
      /^\/credit\/dev\/revalue$/,
      (_m, _q, init) => {
        // FIXTURE-ONLY, and there is no server route of this shape. On the real platform a valuation
        // moves when a valuation officer confirms a new `tokenization.valuation`, which is a
        // tokenization act with its own separation of duties; nothing in the credit domain may set
        // one. This exists so the margin call arc is walkable with the backend off.
        const state = readCredit();
        const body = JSON.parse(String(init?.body ?? '{}'));
        state.valuations[body.token_id] = String(toBaseUnits(String(body.valuation)));
        for (const position of state.positions) {
          if (position.token_id === body.token_id && position.state !== 'released') {
            position.valuation_minor = state.valuations[body.token_id] as string;
            position.eligible_minor = eligibleMinor(position);
          }
        }
        writeCredit(state);
        return { ok: true };
      },
    ],

    [
      'POST',
      /^\/admin\/credit\/sweep-coverage$/,
      () => {
        const state = readCredit();
        const issued: Record<string, unknown>[] = [];
        const cured: Record<string, unknown>[] = [];
        const expired: Record<string, unknown>[] = [];

        for (const loan of state.loans) {
          const id = String(loan['id']);
          if (!['disbursed', 'repaying', 'in_arrears', 'in_grace'].includes(String(loan['state'])))
            continue;

          const coverage = loanCoverage(state, id);
          const ltv = Number(coverage['ltv_bps']);
          const open = state.marginCalls.find((c) => c['loan_id'] === id && c['state'] === 'open');

          if (!open && ltv > Number(coverage['margin_call_bps'])) {
            const product = CREDIT_PRODUCTS.find((p) => p.id === loan['product_id']);
            const call = {
              id: `mc-${state.seq}`,
              loan_id: id,
              issued_at: iso(),
              ltv_at_issue_bps: ltv,
              shortfall_minor: coverage['shortfall_minor'],
              shortfall_asset: 'KES',
              cure_deadline: iso((product?.cure_period_hours ?? 48) * HOUR),
              state: 'open',
              resolved_at: null,
            };
            state.seq += 1;
            state.marginCalls.push(call);
            issued.push(call);
          } else if (open && ltv <= Number(coverage['ceiling_bps'])) {
            open['state'] = 'cured';
            open['resolved_at'] = iso();
            cured.push(open);
          } else if (open && new Date(String(open['cure_deadline'])) <= now()) {
            open['state'] = 'expired';
            open['resolved_at'] = iso();
            expired.push(open);
          }
        }

        writeCredit(state);
        // NOTE what is absent: nothing here declares a default, creates an enforcement, or touches a
        // position. An expired call is a permission, not an outcome, on the stage set as on the real
        // platform.
        return { revalued_loans: [], issued, cured, expired };
      },
    ],

    // -- THE FINANCER SIDE -------------------------------------------------

    [
      'GET',
      /^\/finance\/opportunities$/,
      () => {
        const state = readCredit();
        const open = Object.keys(state.requests).filter((id) =>
          ['open_for_financing', 'financing_interest_received'].includes(
            state.requests[id]['state'] as string,
          ),
        );
        return {
          opportunities: open.map((id) => {
            const request = state.requests[id];
            const product = CREDIT_PRODUCTS.find((p) => p.id === request['product_id']);
            const collateral = eligibleCollateralFor(state, id);
            return {
              request_id: id,
              reference: request['reference'],
              product: {
                id: product?.id,
                name: product?.name ?? null,
                max_ltv_bps: product?.max_ltv_bps ?? 0,
                rate_bps: product?.rate_bps ?? 0,
              },
              amount_minor: request['amount_minor'],
              asset: request['asset'],
              tenor_months: request['tenor_months'],
              repayment_frequency: request['repayment_frequency'],
              purpose: request['purpose'],
              state: request['state'],
              financing_deadline: request['financing_deadline'],
              submitted_at: request['submitted_at'],
              live_offers: state.offers.filter(
                (o) =>
                  o['request_id'] === id &&
                  ['submitted', 'under_review'].includes(o['state'] as string),
              ).length,
              my_offers: state.offers.filter(
                (o) => o['request_id'] === id && o['financer_id'] === DEMO_FINANCER_ID,
              ).length,
              collateral: requestCollateral(state, id),
              eligible_collateral_minor: String(collateral),
              max_financing_minor: String(maxFinancing(state, id)),
              ltv_bps:
                collateral === 0n
                  ? 0
                  : Number((BigInt(String(request['amount_minor'])) * 10000n) / collateral),
              // Thin, on purpose. A marketplace listing is not a reason to disclose a participant's
              // identity to every registered lender.
              borrower: { tier: 'T2', since: iso(-400 * DAY), risk_state: 'normal' },
            };
          }),
        };
      },
    ],

    [
      'GET',
      /^\/finance\/opportunities\/[^/]+$/,
      (m) => {
        const state = readCredit();
        const id = m[0].split('/').pop() as string;
        const detail = requestDetail(state, id);
        // A financer never sees a rival's live terms. That would not be a market, it would be an
        // auction the last bidder always wins.
        detail['offers'] = (detail['offers'] as Record<string, unknown>[]).filter(
          (o) => o['financer_id'] === DEMO_FINANCER_ID,
        );
        return detail;
      },
    ],

    [
      'POST',
      /^\/finance\/opportunities\/[^/]+\/offers$/,
      (m, _q, init) => {
        const state = readCredit();
        const id = m[0].split('/')[3] as string;
        const body = JSON.parse(String(init?.body ?? '{}'));
        const request = state.requests[id];
        if (
          !request ||
          !['open_for_financing', 'financing_interest_received'].includes(
            request['state'] as string,
          )
        ) {
          throw new MockRefusal({
            code: 'APPROVAL_REQUIRED',
            category: 'policy',
            message: 'This borrowing request is not open for financing.',
            retryable: false,
          });
        }
        const amount = String(toBaseUnits(String(body.amount)));
        if (BigInt(amount) > maxFinancing(state, id)) {
          throw new MockRefusal({
            code: 'CAP_EXCEEDED',
            category: 'policy',
            message:
              'That is more than the pledged collateral supports at the product loan to value ceiling.',
            retryable: false,
          });
        }
        const facts = keyFacts(
          body.product_id,
          amount,
          body.rate_bps,
          body.tenor_months,
          body.repayment_frequency,
        );
        const product = CREDIT_PRODUCTS.find((p) => p.id === body.product_id);
        const offer = {
          id: `off-${state.seq}`,
          request_id: id,
          financer_id: DEMO_FINANCER_ID,
          financer_name: 'Rafiki Credit Ltd',
          product_id: body.product_id,
          product_name: product?.name ?? null,
          amount_minor: amount,
          asset: 'KES',
          rate_bps: body.rate_bps,
          tenor_months: body.tenor_months,
          repayment_frequency: body.repayment_frequency,
          fees_minor: String(facts['fees_minor']),
          total_repayable_minor: String(facts['total_repayable_minor']),
          key_facts: facts,
          conditions: body.conditions ?? [],
          state: 'submitted',
          decline_reason: null,
          approved_at: null,
          created_at: iso(),
          expires_at: iso((body.expires_in_days ?? 7) * DAY),
        };
        state.seq += 1;
        state.offers.push(offer);
        if (request['state'] === 'open_for_financing')
          transition(state, id, 'financing_interest_received', 'financer');
        writeCredit(state);
        return offer;
      },
    ],

    [
      'GET',
      /^\/finance\/offers$/,
      () => {
        const state = readCredit();
        return {
          offers: state.offers
            .filter((o) => o['financer_id'] === DEMO_FINANCER_ID)
            .map((o) => {
              const loan = state.loans.find((l) => l['offer_id'] === o['id']);
              const request = state.requests[o['request_id'] as string];
              return {
                ...o,
                request_reference: request?.['reference'] ?? null,
                request_state: request?.['state'] ?? null,
                purpose: request?.['purpose'] ?? null,
                loan_id: (loan?.['id'] as string) ?? null,
                loan_state: (loan?.['state'] as string) ?? null,
                stage: offerStage(o, loan),
              };
            }),
        };
      },
    ],

    [
      'POST',
      /^\/finance\/offers\/[^/]+\/approve$/,
      (m, _q, init) => {
        const state = readCredit();
        const offerId = m[0].split('/')[3] as string;
        const offer = state.offers.find((o) => o['id'] === offerId);
        if (!offer || offer['state'] !== 'accepted') {
          throw new MockRefusal({
            code: 'APPROVAL_REQUIRED',
            category: 'policy',
            message: 'This offer has not been accepted by the borrower and cannot be approved.',
            retryable: false,
          });
        }
        const body = JSON.parse(String(init?.body ?? '{}'));
        const requestId = offer['request_id'] as string;
        const request = state.requests[requestId];
        offer['approved_at'] = iso();

        const principal = BigInt(String(offer['amount_minor']));
        const total = BigInt(String(offer['total_repayable_minor']));
        const fees = BigInt(String(offer['fees_minor']));
        const tenor = Number(offer['tenor_months']);
        const frequency = String(offer['repayment_frequency']);
        const step =
          frequency === 'bullet'
            ? tenor
            : frequency === 'quarterly'
              ? 3
              : frequency === 'seasonal'
                ? 6
                : 1;
        const instalments = frequency === 'bullet' ? 1 : Math.max(1, Math.floor(tenor / step));
        const interestTotal = total - principal - fees;

        const schedule = Array.from({ length: instalments }, (_, i) => {
          const last = i === instalments - 1;
          const principalEach = principal / BigInt(instalments);
          const interestEach = interestTotal / BigInt(instalments);
          const feeEach = fees / BigInt(instalments);
          const p = last ? principal - principalEach * BigInt(instalments - 1) : principalEach;
          const int = last ? interestTotal - interestEach * BigInt(instalments - 1) : interestEach;
          const f = last ? fees - feeEach * BigInt(instalments - 1) : feeEach;
          return {
            n: i + 1,
            due_at: iso((i + 1) * step * 30 * DAY),
            principal_minor: String(p),
            interest_minor: String(int),
            fee_minor: String(f),
            total_minor: String(p + int + f),
            paid_minor: '0',
            state: 'due',
          };
        });

        const loan = {
          id: `loan-${state.seq}`,
          request_id: requestId,
          request_reference: request['reference'],
          offer_id: offerId,
          borrower_id: DEMO_ID,
          borrower_name: null,
          lender_id: offer['financer_id'],
          lender_name: offer['financer_name'],
          product_id: offer['product_id'],
          product_name: offer['product_name'],
          principal_minor: String(principal),
          outstanding_minor: String(principal),
          asset: 'KES',
          // APPROVED, not disbursed. Section 20: a loan is not disbursed because a financer approved it.
          state: 'approved',
          rate_bps: offer['rate_bps'],
          tenor_months: tenor,
          repayment_frequency: frequency,
          fees_minor: String(fees),
          total_repayable_minor: String(total),
          principal_repaid_minor: '0',
          interest_paid_minor: '0',
          fees_paid_minor: '0',
          repaid_pct: 0,
          next_due_at: null,
          arrears_since: null,
          grace_expires_at: null,
          created_at: iso(),
          disbursed_at: null,
          closed_at: null,
          schedule,
          repayments: [] as unknown[],
          approval_notes: body.notes ?? null,
        };
        state.seq += 1;
        state.loans.push(loan);
        for (const position of requestCollateral(state, requestId)) position.loan_id = loan.id;
        transition(state, requestId, 'approved', 'financer', null, body.notes ?? null);
        writeCredit(state);
        return { ...loan, collateral: state.positions.filter((p) => p.loan_id === loan.id) };
      },
    ],

    [
      'POST',
      /^\/finance\/offers\/[^/]+\/decline$/,
      (m, _q, init) => {
        const state = readCredit();
        const offerId = m[0].split('/')[3] as string;
        const body = JSON.parse(String(init?.body ?? '{}'));
        const offer = state.offers.find((o) => o['id'] === offerId);
        if (offer) {
          offer['state'] = 'rejected';
          offer['decline_reason'] = body.reason;
          const requestId = offer['request_id'] as string;
          state.requests[requestId]['selected_offer_id'] = null;
          // Back to the market, not killed. The borrower did nothing wrong and the collateral is still
          // validly pledged.
          transition(state, requestId, 'open_for_financing', 'financer', null, body.reason);
        }
        writeCredit(state);
        return requestDetail(state, offer?.['request_id'] as string);
      },
    ],

    [
      'GET',
      /^\/finance\/portfolio$/,
      () => {
        const state = readCredit();
        const mine = state.loans.filter((l) => l['lender_id'] === DEMO_FINANCER_ID);
        const active = mine.filter((l) =>
          ['disbursed', 'repaying', 'in_arrears', 'in_grace'].includes(l['state'] as string),
        );
        return {
          loans: mine,
          collateral: state.positions
            .filter(
              (p) => p.loan_id && mine.some((l) => l['id'] === p.loan_id) && p.state !== 'released',
            )
            .map((p) => ({
              position_id: p.id,
              loan_id: p.loan_id,
              asset_name: p.asset_name,
              pledged_bps: p.pledged_bps,
              eligible_minor: p.eligible_minor,
              state: p.state,
              loan_state: (mine.find((l) => l['id'] === p.loan_id)?.['state'] as string) ?? '',
              outstanding_minor:
                (mine.find((l) => l['id'] === p.loan_id)?.['outstanding_minor'] as string) ?? '0',
            })),
          recoveries: [],
          summary: {
            active: active.length,
            outstanding_minor: String(
              active.reduce((sum, l) => sum + BigInt(String(l['outstanding_minor'])), 0n),
            ),
            disbursed_minor: String(
              mine
                .filter((l) => l['disbursed_at'])
                .reduce((sum, l) => sum + BigInt(String(l['principal_minor'])), 0n),
            ),
            repaid: mine.filter((l) => l['state'] === 'closed').length,
            defaulted: mine.filter((l) =>
              ['defaulted', 'in_recovery', 'partially_recovered'].includes(l['state'] as string),
            ).length,
            recovered: mine.filter((l) => l['state'] === 'recovered').length,
            in_arrears: mine.filter((l) =>
              ['in_arrears', 'in_grace'].includes(l['state'] as string),
            ).length,
          },
        };
      },
    ],
  ];
}

/**
 * Coverage, computed the way `credit.coverage` computes it.
 *
 * The eligible figure comes from the loan's own positions, each applying its PLEDGED SHARE before the
 * haircut. Applying the haircut to the whole asset would overstate coverage by the unpledged
 * fraction, which is the exact defect migration 0058 had to go back and correct on the server.
 */
function loanCoverage(state: MockCreditState, loanId: string): Record<string, unknown> {
  const loan = state.loans.find((l) => l['id'] === loanId);
  const product = CREDIT_PRODUCTS.find((p) => p.id === loan?.['product_id']);
  const outstanding = BigInt(String(loan?.['outstanding_minor'] ?? '0'));

  const eligible = state.positions
    .filter((p) => p.loan_id === loanId && p.state !== 'released' && p.state !== 'realized')
    .reduce((sum, p) => sum + BigInt(p.eligible_minor), 0n);

  const ceiling = BigInt(product?.max_ltv_bps ?? 6000);
  // No collateral against a positive balance is 100%, never zero and never unknown: "unknown" must
  // not be readable as "fine".
  const ltv = outstanding <= 0n ? 0n : eligible <= 0n ? 10000n : (outstanding * 10000n) / eligible;
  const permitted = (eligible * ceiling) / 10000n;

  return {
    ltv_bps: Number(ltv),
    eligible_minor: String(eligible),
    outstanding_minor: String(outstanding),
    ceiling_bps: Number(ceiling),
    margin_call_bps: product?.margin_call_ltv_bps ?? 6500,
    liquidation_bps: product?.liquidation_ltv_bps ?? 7000,
    shortfall_minor: String(outstanding > permitted ? outstanding - permitted : 0n),
  };
}

/** Close an open call once coverage is back under the CEILING. Not under the trigger: see the engine. */
function cureIfCovered(state: MockCreditState, loanId: string): void {
  const coverage = loanCoverage(state, loanId);
  if (Number(coverage['ltv_bps']) > Number(coverage['ceiling_bps'])) return;
  for (const call of state.marginCalls) {
    if (call['loan_id'] === loanId && call['state'] === 'open') {
      call['state'] = 'cured';
      call['resolved_at'] = iso();
    }
  }
}

/** The stage a financer's dashboard groups an offer under. Mirrors `_offer_stage` in the service. */
function offerStage(
  offer: Record<string, unknown>,
  loan: Record<string, unknown> | undefined,
): string {
  const state = offer['state'] as string;
  if (['rejected', 'expired', 'cancelled'].includes(state)) return 'closed';
  if (['submitted', 'under_review'].includes(state)) return 'interest_expressed';
  if (!offer['approved_at']) return 'awaiting_approval';
  const loanState = loan?.['state'] as string | undefined;
  if (loanState && ['closed', 'recovered', 'partially_recovered'].includes(loanState))
    return 'closed';
  if (
    loanState &&
    ['disbursed', 'repaying', 'in_arrears', 'in_grace', 'defaulted', 'in_recovery'].includes(
      loanState,
    )
  )
    return 'disbursed';
  return 'committed';
}

/** Route a call to a fixture. Adds a little latency so loading and skeleton states are visible. */
export async function mockCall<T>(path: string, init?: RequestInit): Promise<T> {
  const method = (init?.method ?? 'GET').toUpperCase();
  const [rawPath, rawQuery = ''] = path.split('?');
  const query = new URLSearchParams(rawQuery);

  await new Promise((r) => setTimeout(r, 140));

  for (const [routeMethod, regex, handler] of ROUTES) {
    if (routeMethod !== method) continue;
    const m = rawPath.match(regex);
    if (m) return handler(m, query, init) as T;
  }

  // Unmatched: log it (so we notice a screen reaching an endpoint we have not staged) and return an
  // empty-ish success rather than throwing, so one gap does not blank a whole screen.
  if (typeof console !== 'undefined')
    console.warn(`[ui-mocks] no fixture for ${method} ${rawPath}`);
  return {} as T;
}

/**
 * Whether the app is running against fixtures rather than the gateway.
 *
 * Optional-chained because `import.meta.env` exists only under Vite, and this module is also imported
 * by the fixture walkthrough that drives the whole credit flow outside a browser. Without the `?.`
 * that harness cannot even load the file, which would make the one check that proves the flow joins
 * up the one check nobody can run.
 */
export const MOCKS_ON = import.meta.env?.['VITE_UI_MOCKS'] === '1';

void now;
